<template>
  <div class="planner-sheet-form">
    <!-- Initial Selection Modal -->
    <b-modal
      v-model="showSelectionModal"
      title="Select Class, Section, Session and Subject"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      @hidden="onModalHidden"
      @close="onModalClose"
    >
      <template #modal-header>
        <h5 class="modal-title">Select Class, Section, Session and Subject</h5>
        <b-button variant="text" class="btn-close" @click="onModalClose">
          <feather-icon icon="XIcon" />
        </b-button>
      </template>

      <b-overlay :show="dataLoading" rounded="sm">
        <b-form>
          <b-row>
            <b-col md="12">
              <b-form-group
                label="Class *"
                :state="
                  !showValidation ? null : !!sheetObj.plannerSheet.classID
                "
                :invalid-feedback="$t('Class is required')"
              >
                <v-select
                  v-model="sheetObj.plannerSheet.classID"
                  :options="classesOptions"
                  label="name"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select class"
                  @input="onSheetClassChange"
                  :state="
                    !showValidation ? null : !!sheetObj.plannerSheet.classID
                  "
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Section">
                <v-select
                  v-model="sheetObj.plannerSheet.sectionID"
                  :options="currentSections"
                  label="section"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select section"
                  :disabled="!sheetObj.plannerSheet.classID"
                  @input="onSectionChange"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Session *"
                :state="
                  !showValidation ? null : !!sheetObj.plannerSheet.sessionID
                "
                invalid-feedback="Session is required."
              >
                <v-select
                  v-model="sheetObj.plannerSheet.sessionID"
                  :options="sessions"
                  label="session"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select session"
                  :state="
                    !showValidation ? null : !!sheetObj.plannerSheet.sessionID
                  "
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Subject *"
                :state="
                  !showValidation ? null : !!sheetObj.plannerSheet.subjectID
                "
                invalid-feedback="Subject is required."
              >
                <v-select
                  v-model="sheetObj.plannerSheet.subjectID"
                  :options="subjects"
                  label="subject"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select subject"
                  :disabled="isTeacherScoped && !sheetObj.plannerSheet.classID"
                  :state="
                    !showValidation ? null : !!sheetObj.plannerSheet.subjectID
                  "
                />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="text-right mt-2">
            <b-button variant="primary" @click="proceedToForm">
              Proceed
            </b-button>
          </div>
        </b-form>
      </b-overlay>
    </b-modal>

    <!-- Import Modal -->
    <b-modal
      v-model="showImportModal"
      title="Import Planner Sheet"
      size="lg"
      @hidden="resetImportModal"
    >
      <template #modal-header>
        <h5 class="modal-title">Import Planner Sheet</h5>
        <b-button
          variant="text"
          class="btn-close"
          @click="showImportModal = false"
        >
          <feather-icon icon="XIcon" />
        </b-button>
      </template>

      <div class="import-content">
        <!-- File Upload Section -->
        <div class="upload-section mb-4">
          <h6 class="mb-3">
            <feather-icon icon="UploadIcon" class="mr-1" />
            Upload File
          </h6>
          <div class="upload-file-wrapper">
            <b-form-file
              v-model="importFile"
              :state="importFileState"
              placeholder="Select file..."
              drop-placeholder="Drop file here..."
              accept=".xlsx,.xls,.csv"
              class="import-file-input"
              @input="onFileSelected"
            />
            <small class="text-muted mt-2 d-block file-help-text">
              <feather-icon icon="InfoIcon" size="14" class="mr-1" />
              Choose Excel (.xlsx, .xls) or CSV file
            </small>
            <b-form-invalid-feedback :state="importFileState">
              Please select a valid Excel (.xlsx, .xls) or CSV file.
            </b-form-invalid-feedback>
          </div>
        </div>

        <!-- Available Options Info -->

        <!-- Import Progress -->
        <div v-if="importProgress.show" class="import-progress mb-4">
          <h6 class="mb-3">
            <feather-icon icon="ActivityIcon" class="mr-1" />
            Import Progress
          </h6>
          <b-progress
            :value="importProgress.value"
            :max="importProgress.max"
            show-progress
            animated
            :variant="importProgress.variant"
          />
          <div class="mt-2">
            <span class="text-muted">{{ importProgress.message }}</span>
          </div>
        </div>

        <!-- Import Results -->
        <div v-if="importResults.show" class="import-results">
          <h6 class="mb-3">
            <feather-icon icon="CheckCircleIcon" class="mr-1" />
            Import Results
          </h6>
          <b-alert
            :show="importResults.errors.length === 0"
            variant="success"
            class="mb-3"
          >
            <strong>Success!</strong> Successfully imported
            {{ importResults.successCount }} rows.
          </b-alert>

          <b-alert
            :show="importResults.errors.length > 0"
            variant="warning"
            class="mb-3"
          >
            <strong>Warning!</strong> {{ importResults.errors.length }} rows had
            issues.
            <div class="mt-2">
              <small>
                <strong>Note:</strong> Rows with validation errors
                (concept/topic/subtopic mismatches) were skipped. Please ensure
                the names in your file exactly match the dropdown options.
              </small>
            </div>
          </b-alert>

          <!-- Error Details -->
          <div v-if="importResults.errors.length > 0" class="error-details">
            <h6 class="text-danger mb-2">Import Errors:</h6>
            <b-list-group>
              <b-list-group-item
                v-for="(error, index) in importResults.errors.slice(0, 10)"
                :key="index"
                variant="danger"
              >
                <strong>Row {{ error.row }}:</strong> {{ error.message }}
              </b-list-group-item>
            </b-list-group>
            <div
              v-if="importResults.errors.length > 10"
              class="mt-2 text-muted"
            >
              ... and {{ importResults.errors.length - 10 }} more errors
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showImportModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="processImport"
          :disabled="!importFile || importProcessing"
        >
          <b-spinner v-if="importProcessing" small class="mr-1" />
          <feather-icon v-else icon="UploadIcon" class="mr-1" />
          {{ importProcessing ? "Processing..." : "Import Data" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Upload Show Modal -->
    <b-modal
      v-model="showUploadShowModal"
      title="Upload SOW (DOCX)"
      size="lg"
      @hidden="resetUploadShowModal"
    >
      <template #modal-header>
        <h5 class="modal-title">Upload SOW (PDF)</h5>
        <b-button
          variant="text"
          class="btn-close"
          @click="showUploadShowModal = false"
        >
          <feather-icon icon="XIcon" />
        </b-button>
      </template>

      <div class="upload-show-content">
        <!-- File Upload Section -->
        <div class="upload-section mb-4">
          <h6 class="mb-3">
            <feather-icon icon="FileTextIcon" class="mr-1" />
            Upload PDF File
          </h6>
          <div class="upload-file-wrapper">
            <div
              class="planner-file-picker"
              :class="{
                'planner-file-picker--valid': uploadShowFileState === true,
                'planner-file-picker--invalid': uploadShowFileState === false,
              }"
            >
              <div class="planner-file-picker__field">
                <span
                  class="planner-file-picker__name"
                  :class="{ 'text-muted': !uploadShowFile }"
                  :title="uploadShowFileDisplayName"
                >
                  {{ uploadShowFileDisplayName }}
                </span>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="planner-file-picker__btn"
                  @click="$refs.uploadShowInput.click()"
                >
                  Browse
                </b-button>
              </div>
              <input
                ref="uploadShowInput"
                type="file"
                class="d-none"
                accept=".pdf"
                @change="handleUploadShowInputChange"
              />
            </div>
          </div>
          <b-form-invalid-feedback :state="uploadShowFileState">
            Please select a valid PDF file.
          </b-form-invalid-feedback>
          <small class="text-muted mt-2 d-block">
            <feather-icon icon="InfoIcon" size="14" class="mr-1" />
            Only PDF files are allowed. Maximum file size: 10MB
          </small>
        </div>

        <!-- Upload Progress -->
        <div v-if="uploadShowProgress.show" class="upload-progress mb-4">
          <h6 class="mb-3">
            <feather-icon icon="ActivityIcon" class="mr-1" />
            Upload Progress
          </h6>
          <b-progress
            :value="uploadShowProgress.value"
            :max="uploadShowProgress.max"
            show-progress
            animated
            :variant="uploadShowProgress.variant"
          />
          <div class="mt-2">
            <span class="text-muted">{{ uploadShowProgress.message }}</span>
          </div>
        </div>

        <!-- Upload Results -->
        <div v-if="uploadShowResults.show" class="upload-results">
          <h6 class="mb-3">
            <feather-icon icon="CheckCircleIcon" class="mr-1" />
            Upload Results
          </h6>

          <b-alert
            :show="uploadShowResults.success"
            variant="success"
            class="mb-3"
          >
            <strong>Success!</strong> File uploaded successfully.
          </b-alert>

          <b-alert
            :show="!uploadShowResults.success"
            variant="danger"
            class="mb-3"
          >
            <strong>Error!</strong> {{ uploadShowResults.message }}
          </b-alert>

          <!-- Uploaded Link Section -->
          <div
            v-if="uploadShowResults.success && uploadShowResults.link"
            class="uploaded-link-section mt-3"
          >
            <h6 class="mb-3">
              <feather-icon icon="LinkIcon" class="mr-1" />
              Uploaded File Link
            </h6>
            <div class="link-container">
              <div class="d-flex align-items-center mb-2">
                <b-form-input
                  :value="uploadShowResults.link"
                  readonly
                  class="mr-2"
                />
                <div class="link-actions">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="copyLink"
                    v-b-tooltip.hover="'Copy link'"
                  >
                    <feather-icon icon="CopyIcon" size="14" />
                  </b-button>
                  <b-button
                    variant="outline-success"
                    size="sm"
                    @click="openLink"
                    v-b-tooltip.hover="'Open link'"
                  >
                    <feather-icon icon="ExternalLinkIcon" size="14" />
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showUploadShowModal = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="processUploadShow"
          :disabled="!uploadShowFile || uploadShowProcessing"
        >
          <b-spinner v-if="uploadShowProcessing" small class="mr-1" />
          <feather-icon v-else icon="UploadIcon" class="mr-1" />
          {{ uploadShowProcessing ? "Uploading..." : "Upload File" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Upload Urdu Modal -->
    <b-modal
      v-model="showUploadUrduModal"
      title="Upload Urdu (PDF)"
      size="lg"
      @hidden="resetUploadUrduModal"
    >
      <template #modal-header>
        <h5 class="modal-title">Upload Urdu (PDF)</h5>
        <b-button
          variant="text"
          class="btn-close"
          @click="showUploadUrduModal = false"
        >
          <feather-icon icon="XIcon" />
        </b-button>
      </template>

      <div class="upload-show-content">
        <!-- File Upload Section -->
        <div class="upload-section mb-4">
          <h6 class="mb-3">
            <feather-icon icon="FileTextIcon" class="mr-1" />
            Upload PDF File
          </h6>
          <div class="upload-file-wrapper">
            <div
              class="planner-file-picker"
              :class="{
                'planner-file-picker--valid': uploadUrduFileState === true,
                'planner-file-picker--invalid': uploadUrduFileState === false,
              }"
            >
              <div class="planner-file-picker__field">
                <span
                  class="planner-file-picker__name"
                  :class="{ 'text-muted': !uploadUrduFile }"
                  :title="uploadUrduFileDisplayName"
                >
                  {{ uploadUrduFileDisplayName }}
                </span>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="planner-file-picker__btn"
                  @click="$refs.uploadUrduInput.click()"
                >
                  Browse
                </b-button>
              </div>
              <input
                ref="uploadUrduInput"
                type="file"
                class="d-none"
                accept=".pdf"
                @change="handleUploadUrduInputChange"
              />
            </div>
          </div>
          <b-form-invalid-feedback :state="uploadUrduFileState">
            Please select a valid PDF file.
          </b-form-invalid-feedback>
          <small class="text-muted mt-2 d-block">
            <feather-icon icon="InfoIcon" size="14" class="mr-1" />
            Only PDF files are allowed. Maximum file size: 10MB
          </small>
        </div>

        <!-- Upload Progress -->
        <div v-if="uploadUrduProgress.show" class="upload-progress mb-4">
          <h6 class="mb-3">
            <feather-icon icon="ActivityIcon" class="mr-1" />
            Upload Progress
          </h6>
          <b-progress
            :value="uploadUrduProgress.value"
            :max="uploadUrduProgress.max"
            show-progress
            animated
            :variant="uploadUrduProgress.variant"
          />
          <div class="mt-2">
            <span class="text-muted">{{ uploadUrduProgress.message }}</span>
          </div>
        </div>

        <!-- Upload Results -->
        <div v-if="uploadUrduResults.show" class="upload-results">
          <h6 class="mb-3">
            <feather-icon icon="CheckCircleIcon" class="mr-1" />
            Upload Results
          </h6>

          <b-alert
            :show="uploadUrduResults.success"
            variant="success"
            class="mb-3"
          >
            <strong>Success!</strong> File uploaded successfully.
          </b-alert>

          <b-alert
            :show="!uploadUrduResults.success"
            variant="danger"
            class="mb-3"
          >
            <strong>Error!</strong> {{ uploadUrduResults.message }}
          </b-alert>

          <!-- Uploaded Link Section -->
          <div
            v-if="uploadUrduResults.success && uploadUrduResults.link"
            class="uploaded-link-section mt-3"
          >
            <h6 class="mb-3">
              <feather-icon icon="LinkIcon" class="mr-1" />
              Uploaded File Link
            </h6>
            <div class="link-container">
              <div class="d-flex align-items-center mb-2">
                <b-form-input
                  :value="uploadUrduResults.link"
                  readonly
                  class="mr-2"
                />
                <div class="link-actions">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="copyUrduLink"
                    v-b-tooltip.hover="'Copy link'"
                  >
                    <feather-icon icon="CopyIcon" size="14" />
                  </b-button>
                  <b-button
                    variant="outline-success"
                    size="sm"
                    @click="openUrduLink"
                    v-b-tooltip.hover="'Open link'"
                  >
                    <feather-icon icon="ExternalLinkIcon" size="14" />
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showUploadUrduModal = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="processUploadUrdu"
          :disabled="!uploadUrduFile || uploadUrduProcessing"
        >
          <b-spinner v-if="uploadUrduProcessing" small class="mr-1" />
          <feather-icon v-else icon="UploadIcon" class="mr-1" />
          {{ uploadUrduProcessing ? "Uploading..." : "Upload File" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Main Form -->
    <div v-if="showMainForm">
      <b-overlay
        :show="dataLoading"
        rounded="sm"
        spinner-variant="primary"
        spinner-type="grow"
        class="w-100"
      >
        <template #overlay>
          <div
            class="d-flex flex-column align-items-center justify-content-center h-100"
          >
            <b-spinner variant="primary" type="grow" label="Loading..." />
          </div>
        </template>

        <b-row class="mb-2">
          <b-col md="12" class="d-flex align-items-center">
            <b-button
              variant="outline-secondary"
              @click="$router.go(-1)"
              class="btn-icon mr-2 rounded-circle"
            >
              <feather-icon class="" icon="ArrowLeftIcon" />
            </b-button>
            <h4 class="mb-0">{{ pageTitle }}</h4>
          </b-col>
        </b-row>

        <b-card>
          <!-- Add Selected Details -->
          <b-row class="mb-3">
            <b-col md="3">
              <div class="d-flex align-items-center">
                <strong class="mr-1">Class:</strong>
                <span>{{ getSelectedClassName }}</span>
              </div>
            </b-col>
            <b-col md="3">
              <div class="d-flex align-items-center">
                <strong class="mr-1">Section:</strong>
                <span>{{ getSelectedSectionName }}</span>
              </div>
            </b-col>
            <b-col md="3">
              <div class="d-flex align-items-center">
                <strong class="mr-1">Session:</strong>
                <span>{{ getSelectedSessionName }}</span>
              </div>
            </b-col>
            <b-col md="3">
              <div class="d-flex align-items-center">
                <strong class="mr-1">Subject:</strong>
                <span>{{ getSelectedSubjectName }}</span>
              </div>
            </b-col>
          </b-row>

          <!-- Simple Table Implementation -->
          <div class="mt-2">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">Planner Sheet</h5>
              <div class="action-buttons">
                <b-button
                  v-if="uploadedFileLink"
                  variant="outline-success"
                  size="sm"
                  class="mr-2"
                  @click="previewShow"
                  :disabled="saving"
                >
                  <feather-icon icon="EyeIcon" class="mr-1" />
                  Preview SOW
                </b-button>
                <b-button
                  variant="outline-info"
                  size="sm"
                  class="mr-2"
                  @click="openUploadShowModal"
                  :disabled="saving"
                >
                  <feather-icon icon="UploadIcon" class="mr-1" />
                  Upload SOW
                </b-button>
                <b-button
                  v-if="uploadedUrduLink"
                  variant="outline-success"
                  size="sm"
                  class="mr-2"
                  @click="previewUrdu"
                  :disabled="saving"
                >
                  <feather-icon icon="EyeIcon" class="mr-1" />
                  Preview Urdu
                </b-button>
                <b-button
                  variant="outline-info"
                  size="sm"
                  class="mr-2"
                  @click="openUploadUrduModal"
                  :disabled="saving"
                >
                  <feather-icon icon="UploadIcon" class="mr-1" />
                  Upload Urdu
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-2"
                  @click="openImportModal"
                  :disabled="saving"
                >
                  <feather-icon icon="UploadIcon" class="mr-1" />
                  Import
                </b-button>
                <b-button
                  variant="primary"
                  size="sm"
                  @click="saveSheet"
                  :disabled="saving"
                >
                  <b-spinner v-if="saving" small type="grow" />
                  <span v-else>{{ buttonText }}</span>
                </b-button>
              </div>
            </div>

            <div class="simple-planner-table">
              <div class="table-responsive-wrapper">
                <div class="table-scroll-container">
                  <table class="table table-bordered table-hover">
                    <thead class="thead-light sticky-header">
                      <tr>
                        <th class="concept-col">Concept</th>
                        <th class="topic-col">Topic</th>
                        <th class="subtopic-col">Subtopic</th>
                        <th v-if="showPlannerTypeColumn" class="type-col">
                          Type
                        </th>
                        <th
                          v-for="header in plannerHeaders"
                          :key="header.id"
                          class="header-col"
                        >
                          <div class="header-content">
                            {{ header.header }}
                          </div>
                        </th>
                        <th class="actions-col sticky-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-if="dataLoading">
                        <tr class="text-center">
                          <td :colspan="tableColspan" class="py-3">
                            <div
                              class="d-flex justify-content-center align-items-center"
                            >
                              <b-spinner
                                small
                                type="grow"
                                variant="primary"
                                class="mr-1"
                              />
                              <span class="text-primary">Loading data...</span>
                            </div>
                          </td>
                        </tr>
                      </template>
                      <template v-else-if="!plannerRows.length">
                        <tr class="text-center">
                          <td :colspan="tableColspan" class="py-3">
                            <span>No data available</span>
                          </td>
                        </tr>
                      </template>
                      <template v-else>
                        <tr
                          v-for="(row, index) in plannerRows"
                          :key="index"
                          class="data-row"
                        >
                          <td class="concept-col">
                            <v-select
                              v-model="row.concept"
                              :options="concepts"
                              label="concept"
                              :reduce="(concept) => concept.concept"
                              :placeholder="
                                conceptsLoaded
                                  ? 'Select Concept'
                                  : 'Loading concepts...'
                              "
                              :loading="!conceptsLoaded"
                              class="simple-select"
                              :class="{
                                'is-invalid':
                                  !row.concept && showTableValidation,
                              }"
                              @input="onConceptChange(index)"
                            />
                          </td>
                          <td class="topic-col">
                            <v-select
                              v-model="row.topic"
                              :options="topics"
                              label="mainTopic"
                              :reduce="(topic) => topic.mainTopic"
                              @input="onTopicChange(index)"
                              :placeholder="
                                topicsLoaded
                                  ? 'Select Topic'
                                  : 'Loading topics...'
                              "
                              :loading="!topicsLoaded"
                              class="simple-select"
                              :class="{
                                'is-invalid': !row.topic && showTableValidation,
                              }"
                            />
                          </td>
                          <td class="subtopic-col">
                            <v-select
                              v-model="row.subtopic"
                              :options="getSubtopicsForTopic(row.topic)"
                              label="subTopic"
                              :reduce="(subtopic) => subtopic.subTopic"
                              :placeholder="
                                subtopicsLoaded
                                  ? 'Select Subtopic'
                                  : 'Loading subtopics...'
                              "
                              :loading="!subtopicsLoaded"
                              class="simple-select"
                              @input="onSubtopicChange(index)"
                              :class="{
                                'is-invalid':
                                  !row.subtopic && showTableValidation,
                              }"
                            />
                          </td>
                          <td v-if="showPlannerTypeColumn" class="type-col">
                            <v-select
                              v-model="row.type"
                              :options="plannerTypeOptions"
                              :reduce="(option) => option.value"
                              label="label"
                              :clearable="false"
                              placeholder="Select Type"
                              class="simple-select"
                            />
                          </td>
                          <td
                            v-for="header in plannerHeaders"
                            :key="header.id"
                            class="header-col"
                          >
                            <b-form-textarea
                              v-model="row.headers[header.id]"
                              rows="2"
                              placeholder="Enter details..."
                              class="simple-textarea"
                              @input="
                                onTextareaInput($event, row.headers, header.id)
                              "
                            />
                          </td>
                          <td class="text-center actions-col sticky-right">
                            <b-button
                              v-if="index === plannerRows.length - 1"
                              variant="outline-primary"
                              size="sm"
                              class="btn-icon"
                              @click="addNewRow"
                              v-b-tooltip.hover="'Add new row'"
                            >
                              <feather-icon icon="PlusIcon" size="14" />
                            </b-button>
                            <b-button
                              v-else
                              variant="outline-danger"
                              size="sm"
                              class="btn-icon"
                              @click="removeRow(index)"
                              v-b-tooltip.hover="'Remove row'"
                            >
                              <feather-icon icon="TrashIcon" size="14" />
                            </b-button>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BFormTextarea,
  BFormInput,
  BOverlay,
  BModal,
  BForm,
  BFormGroup,
  BAlert,
  BProgress,
  BListGroup,
  BListGroupItem,
  BFormFile,
  BFormInvalidFeedback,
  VBTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import axios from "axios";
import { getUploadPath, uploadToMySkool } from "@/utils/gdriveUpload";
import teacherPlannerScopeMixin from "./teacherPlannerScopeMixin";

export default {
  mixins: [teacherPlannerScopeMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    vSelect,
    BFormTextarea,
    BFormInput,
    BOverlay,
    BModal,
    BForm,
    BFormGroup,
    BAlert,
    BProgress,
    BListGroup,
    BListGroupItem,
    BFormFile,
    BFormInvalidFeedback,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      dataLoading: true,
      otherLoading: false,
      saving: false,
      isEdit: false,
      pageTitle: "Add Planner Sheet",
      buttonText: "Save",

      // Add these properties for dropdown options
      classes: [],
      classesOptions: [],
      currentSections: [],
      sessions: [],
      subjects: [],

      // Planner Headers - will be loaded from API
      plannerHeaders: [],

      // Add concepts data
      concepts: [],

      // Form object with empty initial values
      sheetObj: {
        plannerSheet: {
          id: 0,
          classID: null,
          sectionID: 0, // Default to 0 (All)
          sessionID: null,
          subjectID: null,
          teacherID: 0,
          date: new Date().toJSON(),
          isActive: true,
          campusID: this.$store.state.userData.cId,
          addedBy: null,
          addedOn: null,
          updatedBy: null,
          updatedOn: null,
        },
        plannerSheetDetails: [],
      },

      // Topics and subtopics - will be loaded from API
      topics: [],
      subtopics: [],

      // Add reactive flags to track data loading state
      conceptsLoaded: false,
      topicsLoaded: false,
      subtopicsLoaded: false,

      showSelectionModal: false,
      showMainForm: false,

      plannerRows: [
        {
          concept: "",
          topic: "",
          subtopic: "",
          headers: {},
          conceptId: 0,
          topicId: 0,
          subtopicId: 0,
        },
      ],

      showValidation: false,
      showTableValidation: false,

      // Import related properties
      showImportModal: false,
      importFile: null,
      importFileState: null,
      importProgress: {
        show: false,
        value: 0,
        max: 100,
        message: "",
        variant: "primary",
      },
      importResults: {
        show: false,
        successCount: 0,
        errors: [],
      },
      importProcessing: false,

      // Upload Show related properties
      showUploadShowModal: false,
      uploadShowFile: null,
      uploadShowFileState: null,
      uploadShowProgress: {
        show: false,
        value: 0,
        max: 100,
        message: "",
        variant: "primary",
      },
      uploadShowResults: {
        show: false,
        success: false,
        message: "",
        link: "",
      },
      uploadShowProcessing: false,

      // Store the uploaded file link for Preview Show button
      uploadedFileLink: "",

      // Upload Urdu related properties
      showUploadUrduModal: false,
      uploadUrduFile: null,
      uploadUrduFileState: null,
      uploadUrduProgress: {
        show: false,
        value: 0,
        max: 100,
        message: "",
        variant: "primary",
      },
      uploadUrduResults: {
        show: false,
        success: false,
        message: "",
        link: "",
      },
      uploadUrduProcessing: false,

      // Store the uploaded Urdu file link for Preview Urdu button
      uploadedUrduLink: "",
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    uploadShowFileDisplayName() {
      return this.uploadShowFile && this.uploadShowFile.name
        ? this.uploadShowFile.name
        : "Choose PDF file...";
    },
    uploadUrduFileDisplayName() {
      return this.uploadUrduFile && this.uploadUrduFile.name
        ? this.uploadUrduFile.name
        : "Choose PDF file...";
    },
    showPlannerTypeColumn() {
      return this.reportDomain === "myskoolaba";
    },
    plannerTypeOptions() {
      return [
        { label: "None", value: "None" },
        { label: "Class Test", value: "Class Test" },
        { label: "Quiz", value: "Quiz" },
      ];
    },
    tableColspan() {
      return this.plannerHeaders.length + 4 + (this.showPlannerTypeColumn ? 1 : 0);
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    isSelectionValid() {
      return (
        this.sheetObj.plannerSheet.classID &&
        this.sheetObj.plannerSheet.sessionID &&
        this.sheetObj.plannerSheet.subjectID
      );
    },
    isDropdownDataLoaded() {
      return this.conceptsLoaded && this.topicsLoaded && this.subtopicsLoaded;
    },
    getSelectedClassName() {
      const selectedClass = this.classesOptions.find(
        (c) => c.id === this.sheetObj.plannerSheet.classID
      );
      return selectedClass ? selectedClass.name : "Not Selected";
    },
    getSelectedSectionName() {
      if (this.sheetObj.plannerSheet.sectionID === 0) {
        return "All";
      }
      const selectedSection = this.currentSections.find(
        (s) => s.id === this.sheetObj.plannerSheet.sectionID
      );
      return selectedSection ? selectedSection.section : "All";
    },
    getSelectedSessionName() {
      const selectedSession = this.sessions.find(
        (s) => s.id === this.sheetObj.plannerSheet.sessionID
      );
      return selectedSession ? selectedSession.session : "Not Selected";
    },
    getSelectedSubjectName() {
      const selectedSubject = this.subjects.find(
        (s) => s.id === this.sheetObj.plannerSheet.subjectID
      );
      return selectedSubject ? selectedSubject.subject : "Not Selected";
    },
  },
  async created() {
    // Initialize rights and enforce view/add/edit permissions
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    // Check for rights under the parent route name (planner-sheet) instead of the form route name
    const parentRouteName = "planner-sheet";

    if (!this.$store.state.rights[parentRouteName]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[parentRouteName];

    // If adding and no add right, block
    const isAdd =
      !this.$route.params.id || parseInt(this.$route.params.id) === 0;
    if (isAdd && !this.rights.add) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    // If editing and no edit right, block
    const isEditRoute = !isAdd;
    if (isEditRoute && !this.rights.edit) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    try {
      this.dataLoading = true;

      // Check if we're adding or editing
      const id = parseInt(this.$route.params.id);

      // Load initial data in parallel
      await this.initTeacherPlannerScope();
      await Promise.all([
        this.loadPlannerHeaders(),
        this.loadClasses(),
        this.loadSubjects(),
        this.loadSessions(),
      ]);

      if (id && id !== 0) {
        // Edit mode
        this.isEdit = true;
        this.pageTitle = "Edit Planner Sheet";
        this.buttonText = "Update";
        this.showMainForm = true;
        this.showSelectionModal = false; // Don't show modal in edit mode

        await this.loadSheetData(id);
        await Promise.all([this.loadTopics(), this.loadConcepts()]);
      } else {
        // Add mode - set default section to 0 (All)
        this.isEdit = false;
        this.pageTitle = "Add Planner Sheet";
        this.buttonText = "Save";
        this.sheetObj.plannerSheet.sectionID = 0; // Explicitly set default section
        this.showSelectionModal = true;
        this.showMainForm = false;

        // Initialize sections with "All" option
        this.currentSections = [{ id: 0, section: "All" }];

      }
    } catch (error) {
      console.error("Error in created:", error);
      this.$bvToast.toast("Error loading data!", {
        title: this.$t("Error!"),
        variant: "danger",
        solid: true,
      });
    } finally {
      this.dataLoading = false;
    }
  },
  mounted() {
    this.ensurePlannerRowTypes();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    ensurePlannerRowTypes() {
      if (!this.showPlannerTypeColumn) return;

      this.plannerRows.forEach((row) => {
        if (!row.type) {
          this.$set(row, "type", "None");
        }
      });
    },

    proceedToForm() {
      this.showValidation = true;

      const missingFields = [];
      if (!this.sheetObj.plannerSheet.classID) missingFields.push("Class");
      if (!this.sheetObj.plannerSheet.sessionID) missingFields.push("Session");
      if (!this.sheetObj.plannerSheet.subjectID) missingFields.push("Subject");

      if (missingFields.length > 0) {
        this.$bvToast.toast(`Please select ${missingFields.join(", ")}!`, {
          title: "Required Fields",
          variant: "warning",
          solid: true,
        });
        return;
      }

      // Ensure section is set to 0 if not selected
      if (!this.sheetObj.plannerSheet.sectionID) {
        this.sheetObj.plannerSheet.sectionID = 0;
      }

      this.showSelectionModal = false;
      this.showMainForm = true;
      this.ensurePlannerRowTypes();

      // Load topics and concepts after proceeding
      Promise.all([this.loadTopics(), this.loadConcepts()])
        .then(() => {

          // Debug v-select options
          this.debugVSelectOptions();

          // Force update after a short delay to ensure DOM is updated
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        })
        .catch((error) => {
          console.error("Error loading topics and concepts:", error);
        });
    },

    debugVSelectOptions() {

      // Check if the label fields exist
      if (this.concepts.length > 0) {
      }
      if (this.topics.length > 0) {
      }
      if (this.subtopics.length > 0) {
      }
    },

    async manualLoadData() {
      try {
        await Promise.all([this.loadTopics(), this.loadConcepts()]);
        this.debugVSelectOptions();
      } catch (error) {
        console.error("Error in manual data loading:", error);
      }
    },

    onModalClose() {
      this.showValidation = false;
      this.$router.push("/planner-sheet");
    },

    onModalHidden() {
      this.showValidation = false;
      if (!this.showMainForm) {
        this.$router.push("/planner-sheet");
      }
    },

    buildScopedPlannerResourceUrl(endpoint, classID = null, subjectID = null) {
      let url = `${this.$store.state.domain}${endpoint}?db=${this.$store.state.userData.db}&campusID=${this.plannerCampusID}&page=1&pageSize=1000`;
      const scopeParts = [];
      this.appendTeacherScopeQueryParts(scopeParts, classID || null);
      if (scopeParts.length) {
        url += `&${scopeParts.join("&")}`;
      }
      if (subjectID) {
        url += `&subjectID=${subjectID}`;
      }
      return url;
    },

    onSheetClassChange() {
      if (this.isTeacherScoped) {
        this.sheetObj.plannerSheet.subjectID = null;
      }
      this.loadSections();
    },

    updateSheetSubjects() {
      if (!this.isTeacherScoped) return;
      const classID = this.sheetObj.plannerSheet.classID;
      const sectionID = this.sheetObj.plannerSheet.sectionID;
      this.subjects = classID
        ? this.getAssignedSubjectsForClass(classID, sectionID)
        : [];
      if (
        this.sheetObj.plannerSheet.subjectID &&
        !this.subjects.some(
          (sub) => sub.id === this.sheetObj.plannerSheet.subjectID
        )
      ) {
        this.sheetObj.plannerSheet.subjectID = null;
      }
    },

    async loadTopics() {
      try {
        const classID = this.sheetObj.plannerSheet.classID;
        const subjectID = this.sheetObj.plannerSheet.subjectID;
        const topicsUrl = this.buildScopedPlannerResourceUrl(
          "PlannerMainTopics/GetByPagination",
          classID,
          subjectID
        );
        const subtopicsUrl = this.buildScopedPlannerResourceUrl(
          "PlannerSubTopics/GetByPagination",
          classID,
          subjectID
        );

        const [topicsResponse, subtopicsResponse] = await Promise.all([
          this.get({
            url: topicsUrl,
            token: this.$store.state.userData.token,
          }),
          this.get({
            url: subtopicsUrl,
            token: this.$store.state.userData.token,
          }),
        ]);

        // Process topics - handle both direct array and object with data property
        let topicsData = null;
        if (Array.isArray(topicsResponse)) {
          topicsData = topicsResponse;
        } else if (topicsResponse && Array.isArray(topicsResponse.data)) {
          topicsData = topicsResponse.data;
        }

        if (topicsData && topicsData.length > 0) {
          this.topics = this.filterRowsForTeacherScope(
            topicsData.map((item) => ({
              id: item.id,
              mainTopic: item.topic || item.mainTopic,
              conceptID: item.conceptID || 0,
              classID: item.classID,
              subjectID: item.subjectID,
              sectionID: item.sectionID,
              className: item.className,
              subjectName: item.subjectName,
              conceptName: item.conceptName,
            }))
          );
          this.topicsLoaded = true;
        } else {
          this.topics = [];
          this.topicsLoaded = false;
        }

        // Process subtopics - handle both direct array and object with data property
        let subtopicsData = null;
        if (Array.isArray(subtopicsResponse)) {
          subtopicsData = subtopicsResponse;
        } else if (subtopicsResponse && Array.isArray(subtopicsResponse.data)) {
          subtopicsData = subtopicsResponse.data;
        }

        if (subtopicsData && subtopicsData.length > 0) {
          this.subtopics = this.filterRowsForTeacherScope(
            subtopicsData.map((item) => ({
              id: item.id,
              subTopic: item.topic || item.subTopic,
              conceptID: item.conceptID || 0,
              mainTopicID: item.mainTopicID || 0,
              classID: item.classID,
              subjectID: item.subjectID,
              sectionID: item.sectionID,
              className: item.className,
              subjectName: item.subjectName,
              conceptName: item.conceptName,
            }))
          );
          this.subtopicsLoaded = true;
        } else {
          this.subtopics = [];
          this.subtopicsLoaded = false;
        }

        // Force Vue to update the components
        this.$forceUpdate();
      } catch (error) {
        console.error("Error loading topics and subtopics:", error);
        this.topics = [];
        this.subtopics = [];
        this.$bvToast.toast("Error loading topics and subtopics!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadSheetData(id) {
      try {
        const obj = {
          url: `${this.$store.state.domain}PlannerSheet/LoadSelected/${this.$store.state.userData.cId}/${id}?db=${this.$store.state.userData.db}`,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        if (response) {
          this.sheetObj = {
            plannerSheet: response.plannerSheet || {},
            plannerSheetDetails: response.plannerSheetDetails || [],
          };

          await this.loadSections();

          // Check if SOW data exists and set the uploaded file link
          if (response.plannerSheet && response.plannerSheet.sow) {
            this.uploadedFileLink = response.plannerSheet.sow;
          }

          // Check if Urdu data exists and set the uploaded Urdu file link
          if (response.plannerSheet && response.plannerSheet.urdu) {
            this.uploadedUrduLink = response.plannerSheet.urdu;
          }

          // Process planner details to create rows
          if (
            response.plannerSheetDetails &&
            Array.isArray(response.plannerSheetDetails)
          ) {
            // First load topics and concepts and wait for them to complete
            await Promise.all([this.loadTopics(), this.loadConcepts()]);

            const detailsByRow = {};

            // Group details by conceptID-mainTopicID-subTopicID (and type for ABA)
            response.plannerSheetDetails.forEach((detail) => {
              const detailType = detail.type || "None";
              const key = this.showPlannerTypeColumn
                ? `${detail.conceptID}-${detail.mainTopicID}-${detail.subTopicID}-${detailType}`
                : `${detail.conceptID}-${detail.mainTopicID}-${detail.subTopicID}`;
              if (!detailsByRow[key]) {
                const concept = this.concepts.find(
                  (c) => c.id === detail.conceptID
                );
                const topic = this.topics.find(
                  (t) => t.id === detail.mainTopicID
                );
                const subtopic = this.subtopics.find(
                  (s) => s.id === detail.subTopicID
                );

                // Handle missing subtopic case more gracefully
                let subtopicText = subtopic?.subTopic || "";
                if (!subtopicText && topic) {
                  // Try to find any subtopic related to this topic
                  const relatedSubtopic = this.subtopics.find(
                    (s) => s.mainTopicID === detail.mainTopicID
                  );
                  if (relatedSubtopic) {
                    subtopicText = relatedSubtopic.subTopic;
                  } else {
                  }
                }

                detailsByRow[key] = {
                  concept: concept?.concept || "",
                  topic: topic?.mainTopic || "",
                  subtopic: subtopicText,
                  headers: {},
                  conceptId: detail.conceptID,
                  topicId: detail.mainTopicID,
                  subtopicId: detail.subTopicID,
                };

                if (this.showPlannerTypeColumn) {
                  detailsByRow[key].type = detailType;
                }
              }

              // Store the remarks in headers object
              detailsByRow[key].headers[detail.headerID] =
                this.processContentForEditing(detail.remarks || "");
            });

            // Convert to array and ensure all header fields exist
            this.plannerRows = Object.values(detailsByRow).map((row) => {
              if (this.showPlannerTypeColumn && !row.type) {
                row.type = "None";
              }
              // Ensure all headers have a value (even if empty)
              this.plannerHeaders.forEach((header) => {
                if (row.headers[header.id] === undefined) {
                  row.headers[header.id] = "";
                }
              });

              // Additional validation for edit mode
              if (!row.concept) {
              }
              if (!row.topic) {
              }
              if (!row.subtopic) {
                // Try to find a default subtopic for the topic
                if (row.topicId) {
                  const defaultSubtopic = this.subtopics.find(
                    (s) => s.mainTopicID === row.topicId
                  );
                  if (defaultSubtopic) {
                    row.subtopic = defaultSubtopic.subTopic;
                    row.subtopicId = defaultSubtopic.id;
                  }
                }
              }

              return row;
            });

            // Filter out completely invalid rows (rows with no concept, topic, or subtopic)
            const validRows = this.plannerRows.filter(
              (row) =>
                (row.concept && String(row.concept).trim() !== "") ||
                (row.topic && String(row.topic).trim() !== "") ||
                (row.subtopic && String(row.subtopic).trim() !== "")
            );

            if (validRows.length !== this.plannerRows.length) {
              this.plannerRows = validRows;
            }

            // Add empty row if no rows exist
            if (this.plannerRows.length === 0) {
              this.addNewRow();
            }

            this.ensurePlannerRowTypes();

            // Force update after processing
            this.$nextTick(() => {
              this.$forceUpdate();
            });
          }
        }
      } catch (error) {
        console.error("Error loading sheet data:", error);
        this.$bvToast.toast("Error loading sheet data!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    resetForm() {
      this.sheetObj = {
        plannerSheet: {
          id: 0,
          classID: 0,
          sectionID: 0,
          sessionID: null,
          subjectID: 0,
          teacherID: 0,
          date: new Date().toJSON(),
          isActive: true,
          campusID: this.$store.state.userData.cId,
          addedBy: null,
          addedOn: null,
          updatedBy: null,
          updatedOn: null,
        },
        plannerSheetDetails: this.plannerHeaders.map((header) => ({
          id: 0,
          plannerSheetID: 0,
          conceptID: 0,
          mainTopicID: 0,
          subTopicID: 0,
          headerID: header.id,
          remarks: "",
        })),
      };
    },

    getHeaderValue(headerId) {
      const detail = this.sheetObj.plannerSheetDetails.find(
        (d) => d.headerID === headerId
      );
      return detail ? detail.remarks : "";
    },

    updateHeaderValue(headerId, value) {
      let detail = this.sheetObj.plannerSheetDetails.find(
        (d) => d.headerID === headerId
      );
      if (!detail) {
        detail = {
          id: 0,
          plannerSheetID: this.sheetObj.plannerSheet.id,
          conceptID: 0,
          mainTopicID: 0,
          subTopicID: 0,
          headerID: headerId,
          remarks: value,
        };
        this.sheetObj.plannerSheetDetails.push(detail);
      } else {
        detail.remarks = value;
      }
    },

    validateForm() {
      // Check if initial selection is valid
      if (!this.isSelectionValid) {
        this.$bvToast.toast("Please select Class, Session and Subject!", {
          title: "Validation Error",
          variant: "danger",
          solid: true,
        });
        return false;
      }

      // Show table validation
      this.showTableValidation = true;

      // Debug logging for validation

      // In edit mode, ensure dropdown data is loaded before validation
      if (
        this.isEdit &&
        (!this.conceptsLoaded || !this.topicsLoaded || !this.subtopicsLoaded)
      ) {
        this.$bvToast.toast(
          "Data is still loading, please wait a moment before saving...",
          {
            title: "Loading Data",
            variant: "info",
            solid: true,
          }
        );
        return false;
      }

      // Filter out completely empty rows (all fields empty)
      const nonEmptyRows = this.plannerRows.filter((row) => {
        const hasConcept = row.concept && String(row.concept).trim() !== "";
        const hasTopic = row.topic && String(row.topic).trim() !== "";
        const hasSubtopic = row.subtopic && String(row.subtopic).trim() !== "";
        const hasAnyHeader = Object.values(row.headers || {}).some(
          (value) => value && String(value).trim() !== ""
        );

        return hasConcept || hasTopic || hasSubtopic || hasAnyHeader;
      });

      if (nonEmptyRows.length === 0) {
        this.$bvToast.toast("Please add at least one row with data!", {
          title: "No Data",
          variant: "warning",
          solid: true,
        });
        return false;
      }

      // Check if we have at least one valid row
      const hasValidRow = nonEmptyRows.some((row) => {
        const hasValidConcept =
          row.concept && String(row.concept).trim() !== "";
        const hasValidTopic = row.topic && String(row.topic).trim() !== "";
        const hasValidSubtopic =
          row.subtopic && String(row.subtopic).trim() !== "";
        const hasValidHeader = Object.values(row.headers || {}).some(
          (value) => value && String(value).trim() !== ""
        );

        const isValid =
          hasValidConcept &&
          hasValidTopic &&
          hasValidSubtopic &&
          hasValidHeader;

        return isValid;
      });

      if (!hasValidRow) {
        this.$bvToast.toast(
          "Please add at least one complete row with Concept, Topic, Subtopic and Header details!",
          {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          }
        );
        return false;
      }

      // Check for incomplete rows (only among non-empty rows) - IMPROVED LOGIC
      const incompleteRows = nonEmptyRows.filter((row) => {
        const hasConcept = row.concept && String(row.concept).trim() !== "";
        const hasTopic = row.topic && String(row.topic).trim() !== "";
        const hasSubtopic = row.subtopic && String(row.subtopic).trim() !== "";
        const hasHeader = Object.values(row.headers || {}).some(
          (value) => value && String(value).trim() !== ""
        );

        // If any field is filled, all required fields should be filled
        if (hasConcept || hasTopic || hasSubtopic || hasHeader) {
          const isIncomplete = !hasConcept || !hasTopic || !hasSubtopic;
          return isIncomplete;
        }
        return false;
      });

      if (incompleteRows.length > 0) {

        // Try to auto-fix missing subtopics in edit mode
        if (this.isEdit && this.subtopics.length > 0) {
          let autoFixed = false;
          incompleteRows.forEach((row) => {
            if (row.concept && row.topic && !row.subtopic) {
              // Try to find a matching subtopic based on the topic
              const matchingSubtopic = this.subtopics.find((s) => {
                const topic = this.topics.find(
                  (t) => t.mainTopic === row.topic
                );
                return topic && s.mainTopicID === topic.id;
              });

              if (matchingSubtopic) {
                row.subtopic = matchingSubtopic.subTopic;
                row.subtopicId = matchingSubtopic.id;
                autoFixed = true;
              }
            }
          });

          if (autoFixed) {
            this.$bvToast.toast(
              "Some missing subtopics were automatically filled. Please verify the data.",
              {
                title: "Auto-filled Data",
                variant: "info",
                solid: true,
              }
            );
            // Re-validate after auto-fix
            return this.validateForm();
          }
        }

        // Show specific error for remaining incomplete rows
        const incompleteDetails = incompleteRows
          .map((row, index) => {
            const missing = [];
            if (!row.concept || String(row.concept).trim() === "")
              missing.push("Concept");
            if (!row.topic || String(row.topic).trim() === "")
              missing.push("Topic");
            if (!row.subtopic || String(row.subtopic).trim() === "")
              missing.push("Subtopic");
            return `Row ${
              this.plannerRows.indexOf(row) + 1
            }: Missing ${missing.join(", ")}`;
          })
          .slice(0, 3); // Show only first 3 errors

        this.$bvToast.toast(
          `Please complete the following fields:\n${incompleteDetails.join(
            "\n"
          )}${
            incompleteRows.length > 3
              ? `\n... and ${incompleteRows.length - 3} more`
              : ""
          }`,
          {
            title: "Incomplete Data",
            variant: "danger",
            solid: true,
            autoHideDelay: 8000,
          }
        );
        return false;
      }

      return true;
    },

    async saveSheet() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;

      try {

        // Convert valid rows to API format
        this.sheetObj.plannerSheetDetails = this.plannerRows
          .filter((row) => row.concept && row.topic && row.subtopic) // Include concept, topic, and subtopic in filter
          .flatMap((row) => {
            // Use flexible matching to find concepts, topics, and subtopics
            const concept = this.findConceptByName(row.concept);
            const topic = this.findTopicByName(row.topic);
            const subtopic = this.findSubtopicByName(row.subtopic);

            if (!concept) {
              console.error(`Concept not found: "${row.concept}"`);
            }
            if (!topic) {
              console.error(`Topic not found: "${row.topic}"`);
            }
            if (!subtopic) {
              console.error(`Subtopic not found: "${row.subtopic}"`);
            }

            return Object.entries(row.headers)
              .filter(([_, value]) => value && value.trim() !== "") // Only include non-empty headers
              .map(([headerId, remarks]) => ({
                id: 0,
                plannerSheetID: this.sheetObj.plannerSheet.id,
                conceptID: concept?.id || 0,
                mainTopicID: topic?.id || 0,
                subTopicID: subtopic?.id || 0,
                headerID: parseInt(headerId),
                remarks: this.processContentForSaving(remarks.trim()), // Process URLs before saving
                ...(this.showPlannerTypeColumn
                  ? { type: row.type || "None" }
                  : {}),
              }));
          });

        // Add required fields to plannerSheet
        this.sheetObj.plannerSheet.teacherID =
          this.$store.state.userData.userID;
        this.sheetObj.plannerSheet.addedBy = this.$store.state.userData.userID;
        this.sheetObj.plannerSheet.addedOn = new Date().toISOString();

        const obj = {
          url: `${this.$store.state.domain}PlannerSheet/Save/${this.$store.state.userData.cId}?db=${this.$store.state.userData.db}&userID=${this.$store.state.userData.userID}`,
          body: this.sheetObj,
          message: this.isEdit
            ? "Planner sheet updated successfully!"
            : "Planner sheet added successfully!",
          context: this,
          token: this.$store.state.userData.token,
        };

        const success = await this.post(obj);

        if (success) {
          this.$bvToast.toast(obj.message, {
            title: "Success!",
            variant: "success",
            solid: true,
          });
          this.$router.go(-1);
        }
      } catch (error) {
        console.error("Error saving sheet:", error);
        this.$bvToast.toast("An error occurred while saving!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    async loadSections() {
      try {

        // Always start with "All" option
        this.currentSections = [{ id: 0, section: "All" }];

        if (this.sheetObj.plannerSheet.classID) {
          const classData = this.classes.find(
            (elem) => elem.cls.id === this.sheetObj.plannerSheet.classID
          );
          if (classData && classData.sections.length > 0) {
            this.currentSections = [
              { id: 0, section: "All" }, // Keep "All" as first option
              ...classData.sections,
            ];
          } else {
          }

          // Set default section to 0 if not already set or if it's null/undefined
          if (
            this.sheetObj.plannerSheet.sectionID === null ||
            this.sheetObj.plannerSheet.sectionID === undefined
          ) {
            this.sheetObj.plannerSheet.sectionID = 0;
          }
        } else {
          // If no class is selected, ensure section is set to 0
          this.sheetObj.plannerSheet.sectionID = 0;
        }

        this.updateSheetSubjects();

      } catch (error) {
        console.error("Error in loadSections:", error);
        this.$bvToast.toast("Error loading sections!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadClasses() {
      try {
        if (this.isTeacherScoped) {
          await this.loadTeacherClasses();
          this.classes = this.normalizeTeacherClassesToLoadFormat();
          this.classesOptions = this.classes.map((el) => el.cls);
          return;
        }

        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        this.classes = await this.get(obj);
        this.classesOptions = this.classes.map((el) => el.cls);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.$bvToast.toast("Error loading classes!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadSessions() {
      try {
        const url = `${this.$store.state.domain}Sessions?db=${this.$store.state.userData.db}&cID=${this.plannerCampusID}`;
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        if (res.data && res.data.status === "success" && Array.isArray(res.data.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data?.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data)) {
          this.sessions = res.data;
        } else {
          this.sessions = [];
        }

        if (
          !this.isEdit &&
          !this.sheetObj.plannerSheet.sessionID &&
          this.sessions.length
        ) {
          const currentSession = this.sessions.find(
            (el) => el.isCurrentSession === true
          );
          if (currentSession) {
            this.sheetObj.plannerSheet.sessionID = currentSession.id;
          } else {
            this.sheetObj.plannerSheet.sessionID =
              this.sessions[this.sessions.length - 1].id;
          }
        }
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
        this.$bvToast.toast("Error loading sessions!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadSubjects() {
      try {
        if (this.isTeacherScoped) {
          this.updateSheetSubjects();
          return;
        }

        const obj = {
          url: `${this.$store.state.domain}Subjects?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
        };
        this.subjects = await this.get(obj);
      } catch (error) {
        console.error("Error loading subjects:", error);
      }
    },

    async loadConcepts() {
      try {
        const classID = this.sheetObj.plannerSheet.classID;
        const subjectID = this.sheetObj.plannerSheet.subjectID;
        const obj = {
          url: this.buildScopedPlannerResourceUrl(
            "PlannerConcepts/GetByPagination",
            classID,
            subjectID
          ),
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);

        let conceptsData = null;
        if (Array.isArray(response)) {
          conceptsData = response;
        } else if (response && Array.isArray(response.data)) {
          conceptsData = response.data;
        }

        if (conceptsData && conceptsData.length > 0) {
          this.concepts = this.filterRowsForTeacherScope(
            conceptsData.map((item) => ({
              ...item,
              concept: this.cleanConceptText(item.concept),
            }))
          );
          this.conceptsLoaded = true;
        } else {
          this.concepts = [];
          this.conceptsLoaded = false;
        }

        // Force Vue to update the components
        this.$forceUpdate();
      } catch (error) {
        console.error("Error loading concepts:", error);
        this.$bvToast.toast("Error loading concepts!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.concepts = [];
      }
    },

    addNewRow() {
      const row = {
        concept: "",
        topic: "",
        subtopic: "",
        headers: {},
        conceptId: 0,
        topicId: 0,
        subtopicId: 0,
      };

      if (this.showPlannerTypeColumn) {
        row.type = "None";
      }

      this.plannerRows.push(row);
    },

    removeRow(index) {
      this.plannerRows.splice(index, 1);
    },

    onTopicChange(index) {
      const row = this.plannerRows[index];
      if (!row) return;

      row.subtopic = ""; // Reset subtopic when topic changes
      const topic = this.topics.find((t) => t.mainTopic === row.topic);
      row.topicId = topic?.id || 0;
    },

    onSubtopicChange(index) {
      const row = this.plannerRows[index];
      if (!row) return;

      const subtopic = this.subtopics.find((s) => s.subTopic === row.subtopic);
      row.subtopicId = subtopic?.id || 0;
    },

    onConceptChange(index) {
      const row = this.plannerRows[index];
      if (!row) return;

      const concept = this.concepts.find((c) => c.concept === row.concept);
      row.conceptId = concept?.id || 0;
    },

    getSubtopicsForTopic(topicName) {
      // Return all subtopics without filtering by topic
      return this.subtopics;
    },

    async loadPlannerHeaders() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerHeaders?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        let res = await this.get(obj);
        if (res && Array.isArray(res)) {
          this.plannerHeaders = res;
        }
      } catch (error) {
        console.error("Error loading planner headers:", error);
      }
    },

    onSectionChange() {
      this.updateSheetSubjects();
      if (
        this.sheetObj.plannerSheet.classID &&
        this.sheetObj.plannerSheet.subjectID
      ) {
        this.loadConcepts();
      }
    },

    cleanConceptText(text) {
      if (!text) return "";

      // Remove class prefixes like "VIII - ", "IX - ", "Class 8 - ", etc.
      let cleanText = text
        .replace(/^(VIII|IX|VII|VI|V|IV|III|II|I)\s*-\s*/i, "") // Roman numerals
        .replace(/^(Class\s+\d+|Grade\s+\d+)\s*-\s*/i, "") // Class/Grade numbers
        .replace(/^(\d+)\s*-\s*/, "") // Plain numbers
        .trim();

      return cleanText;
    },

    // Helper methods for flexible matching
    findConceptByName(name) {
      if (!name || !this.concepts || !Array.isArray(this.concepts)) return null;

      const searchName = name.toLowerCase().trim();

      return this.concepts.find((c) => {
        if (!c || !c.concept || typeof c.concept !== "string") return false;

        const conceptName = c.concept.toLowerCase().trim();

        // Direct match
        if (conceptName === searchName) return true;

        // Try matching with class prefix
        const conceptWithPrefix = `${this.getSelectedClassName} - ${searchName}`;
        if (conceptName === conceptWithPrefix.toLowerCase().trim()) return true;

        // Try matching without class prefix
        const conceptWithoutPrefix = this.cleanConceptText(c.concept)
          .toLowerCase()
          .trim();
        if (conceptWithoutPrefix === searchName) return true;

        return false;
      });
    },

    findTopicByName(name) {
      if (!name || !this.topics || !Array.isArray(this.topics)) return null;

      const searchName = name.toLowerCase().trim();

      return this.topics.find((t) => {
        if (!t || !t.mainTopic || typeof t.mainTopic !== "string") return false;

        const topicName = t.mainTopic.toLowerCase().trim();

        // Direct match
        if (topicName === searchName) return true;

        // Try matching with class prefix
        const topicWithPrefix = `${this.getSelectedClassName} - ${searchName}`;
        if (topicName === topicWithPrefix.toLowerCase().trim()) return true;

        // Try matching without class prefix
        const topicWithoutPrefix = this.cleanConceptText(t.mainTopic)
          .toLowerCase()
          .trim();
        if (topicWithoutPrefix === searchName) return true;

        return false;
      });
    },

    findSubtopicByName(name) {
      if (!name || !this.subtopics || !Array.isArray(this.subtopics))
        return null;

      const searchName = name.toLowerCase().trim();

      return this.subtopics.find((s) => {
        if (!s || !s.subTopic || typeof s.subTopic !== "string") return false;

        const subtopicName = s.subTopic.toLowerCase().trim();

        // Direct match
        if (subtopicName === searchName) return true;

        // Try matching with class prefix
        const subtopicWithPrefix = `${this.getSelectedClassName} - ${searchName}`;
        if (subtopicName === subtopicWithPrefix.toLowerCase().trim())
          return true;

        // Try matching without class prefix
        const subtopicWithoutPrefix = this.cleanConceptText(s.subTopic)
          .toLowerCase()
          .trim();
        if (subtopicWithoutPrefix === searchName) return true;

        return false;
      });
    },

    // Import related methods
    openImportModal() {
      this.showImportModal = true;
    },
    resetImportModal() {
      this.importFile = null;
      this.importFileState = null;
      this.importProgress = {
        show: false,
        value: 0,
        max: 100,
        message: "",
        variant: "primary",
      };
      this.importResults = {
        show: false,
        successCount: 0,
        errors: [],
      };
      this.importProcessing = false;
    },
    onFileSelected() {
      if (this.importFile) {
        // Validate file type
        const allowedExtensions = [".xlsx", ".xls", ".csv"];
        const fileExtension = this.importFile.name
          .toLowerCase()
          .substr(this.importFile.name.lastIndexOf("."));

        if (allowedExtensions.includes(fileExtension)) {
          this.importFileState = true;
        } else {
          this.importFileState = false;
          this.$bvToast.toast(
            "Please select a valid Excel (.xlsx, .xls) or CSV file.",
            {
              title: "Invalid File",
              variant: "danger",
              solid: true,
            }
          );
        }
      } else {
        this.importFileState = null;
      }
    },
    async processImport() {
      if (!this.importFile) {
        this.$bvToast.toast("Please select a file to import.", {
          title: "No File Selected",
          variant: "warning",
          solid: true,
        });
        return;
      }

      // Check if dropdown data is loaded
      if (!this.conceptsLoaded || !this.topicsLoaded || !this.subtopicsLoaded) {
        this.$bvToast.toast(
          "Dropdown data is still loading. Please wait a moment and try again.",
          {
            title: "Data Loading",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      // Check if we have dropdown options
      if (
        this.concepts.length === 0 ||
        this.topics.length === 0 ||
        this.subtopics.length === 0
      ) {
        this.$bvToast.toast(
          "No dropdown options available. Please ensure you have selected a valid class, section, and subject.",
          {
            title: "No Options Available",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      // Debug: Log available concepts for troubleshooting

      this.importProcessing = true;
      this.importProgress.show = true;
      this.importProgress.value = 0;
      this.importProgress.message = "Starting import...";
      this.importResults.show = false;

      try {
        // Read file content
        const fileContent = await this.readFileContent(this.importFile);
        this.importProgress.value = 25;
        this.importProgress.message = "Reading file content...";

        // Parse data based on file type
        let parsedData;
        const fileExtension = this.importFile.name
          .toLowerCase()
          .substr(this.importFile.name.lastIndexOf("."));

        if (fileExtension === ".csv") {
          parsedData = this.parseCSVData(fileContent);
        } else {
          parsedData = await this.parseExcelData(fileContent);
        }

        this.importProgress.value = 50;
        this.importProgress.message = "Validating headers...";

        // Validate headers
        const headerValidation = this.validateHeaders(parsedData.headers);
        if (!headerValidation.valid) {
          throw new Error(
            `Header validation failed: ${headerValidation.message}`
          );
        }

        this.importProgress.value = 60;
        this.importProgress.message =
          "Checking for missing concepts/topics/subtopics...";

        // 1. Collect missing items
        const missingItems = this.processImportData(
          parsedData.data,
          parsedData.headers,
          true
        );
        if (missingItems.length > 0) {
          this.importProgress.value = 70;
          this.importProgress.message =
            "Validating missing items with server...";
          // 2. Call backend to validate/add missing items
          await this.validateMissingItems(missingItems);
          // 3. Reload dropdowns
          await Promise.all([
            this.loadConcepts(),
            this.loadTopics(),
            // subtopics are loaded in loadTopics
          ]);
        }

        this.importProgress.value = 75;
        this.importProgress.message = "Processing data...";

        // 4. Process and validate data again (now with updated dropdowns)
        const processResult = this.processImportData(
          parsedData.data,
          parsedData.headers,
          false
        );

        this.importProgress.value = 100;
        this.importProgress.message = "Import completed!";
        this.importProgress.variant =
          processResult.errors.length > 0 ? "warning" : "success";

        // Show results
        this.importResults = {
          show: true,
          successCount: processResult.successCount,
          errors: processResult.errors,
        };

        // Update table data if successful
        if (processResult.successCount > 0) {
          // Map the imported data to the existing plannerRows format
          this.plannerRows = processResult.validRows.map((validRow) => {
            // Use flexible matching to find the actual concept, topic, and subtopic names
            const concept = this.findConceptByName(validRow.concept);
            const topic = this.findTopicByName(validRow.topic);
            const subtopic = this.findSubtopicByName(validRow.subtopic);

            const row = {
              concept: concept ? concept.concept : validRow.concept,
              topic: topic ? topic.mainTopic : validRow.topic,
              subtopic: subtopic ? subtopic.subTopic : validRow.subtopic,
              headers: {},
              conceptId: concept ? concept.id : 0,
              topicId: topic ? topic.id : 0,
              subtopicId: subtopic ? subtopic.id : 0,
            };

            if (this.showPlannerTypeColumn) {
              row.type = validRow.type || "None";
            }

            return row;
          });

          // Map the planner row headers to the headers object
          this.plannerRows.forEach((row, index) => {
            const validRow = processResult.validRows[index];
            if (validRow && validRow.plannerRowHeaders) {
              validRow.plannerRowHeaders.forEach((header) => {
                row.headers[header.headerID] = header.value;
              });
            }
          });

          this.ensurePlannerRowTypes();

          this.$bvToast.toast(
            `Successfully imported ${processResult.successCount} rows${
              processResult.errors.length > 0
                ? ` with ${processResult.errors.length} errors`
                : ""
            }.`,
            {
              title: "Import Successful",
              variant: processResult.errors.length > 0 ? "warning" : "success",
              solid: true,
            }
          );

          // Only auto-close modal if there are NO errors
          if (processResult.errors.length === 0) {
            setTimeout(() => {
              this.showImportModal = false;
              this.importFile = null;
              this.importFileState = null;
              this.importProgress.show = false;
              this.importResults.show = false;
            }, 1500); // 1.5 second delay to let user see the success message
          }
        } else if (processResult.errors.length > 0) {
          // Show error message if no valid rows were imported
          this.$bvToast.toast(
            "No valid rows were imported. Please check the error details and fix your file.",
            {
              title: "Import Failed",
              variant: "danger",
              solid: true,
            }
          );
        }
      } catch (error) {
        console.error("Import error:", error);
        this.importProgress.variant = "danger";
        this.importProgress.message = "Import failed!";

        this.$bvToast.toast(
          error.message || "An error occurred while importing the file.",
          {
            title: "Import Failed",
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.importProcessing = false;
      }
    },

    readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error("Failed to read file"));

        const fileExtension = file.name
          .toLowerCase()
          .substr(file.name.lastIndexOf("."));
        if (fileExtension === ".csv") {
          reader.readAsText(file);
        } else {
          reader.readAsArrayBuffer(file);
        }
      });
    },

    async parseExcelData(arrayBuffer) {
      try {
        if (!arrayBuffer) {
          throw new Error("No file data provided");
        }

        // Try to load XLSX library dynamically
        let XLSX;

        // First try to import using ES6 import
        try {
          const xlsxModule = await import("xlsx");
          XLSX = xlsxModule.default || xlsxModule;
        } catch (importError) {

          // Fallback to global XLSX
          if (typeof window !== "undefined" && window.XLSX) {
            XLSX = window.XLSX;
          } else if (typeof global !== "undefined" && global.XLSX) {
            XLSX = global.XLSX;
          } else {
            // Try requiring it (for Node-like environments)
            try {
              XLSX = require("xlsx");
            } catch (requireError) {
              console.error("All XLSX loading methods failed");
              throw new Error(
                "Excel parsing library not available. Please use CSV format instead or ensure the XLSX library is installed."
              );
            }
          }
        }

        if (!XLSX || typeof XLSX.read !== "function") {
          throw new Error(
            "Excel parsing library not properly loaded. Please use CSV format instead."
          );
        }

        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          throw new Error("Excel file contains no worksheets");
        }

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        if (!worksheet) {
          throw new Error("Unable to read the first worksheet");
        }

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          blankrows: false,
        });

        if (!Array.isArray(jsonData) || jsonData.length === 0) {
          throw new Error("Excel worksheet is empty or invalid format");
        }

        const headers = jsonData[0] || [];
        if (!Array.isArray(headers) || headers.length === 0) {
          throw new Error("No headers found in Excel file");
        }

        // Clean and validate headers
        const cleanHeaders = headers
          .map((header) =>
            header && typeof header === "string"
              ? header.trim()
              : String(header || "").trim()
          )
          .filter((header) => header !== undefined && header !== null);

        if (cleanHeaders.length === 0) {
          throw new Error("No valid headers found in Excel file");
        }

        // Process data rows with better error handling
        const data = [];
        for (let i = 1; i < jsonData.length; i++) {
          try {
            const row = jsonData[i];
            if (!Array.isArray(row)) {
              continue;
            }

            // Clean row data and ensure it has the right length
            const cleanRow = row.map((cell) =>
              cell !== undefined && cell !== null ? String(cell).trim() : ""
            );

            // Ensure row has the same number of columns as headers
            while (cleanRow.length < cleanHeaders.length) {
              cleanRow.push("");
            }

            // Only include rows that have at least one non-empty cell
            if (cleanRow.some((cell) => cell && String(cell).trim() !== "")) {
              data.push(cleanRow.slice(0, cleanHeaders.length)); // Trim to header length
            }
          } catch (rowError) {
            // Continue with next row instead of failing completely
          }
        }

        return { headers: cleanHeaders, data };
      } catch (error) {
        console.error("Excel parsing error:", error);
        throw new Error(`Failed to parse Excel file: ${error.message}`);
      }
    },

    parseCSVData(csvContent) {
      try {
        if (!csvContent || typeof csvContent !== "string") {
          throw new Error("Invalid CSV content: content must be a string");
        }

        const lines = csvContent
          .split(/\r?\n/)
          .filter((line) => line && line.trim());
        if (lines.length === 0) {
          throw new Error("CSV file is empty");
        }

        // Parse headers - handle both comma and semicolon separators
        let separator = ",";
        if (lines[0].split(";").length > lines[0].split(",").length) {
          separator = ";";
        }

        const headers = lines[0]
          .split(separator)
          .map(
            (header) =>
              header && header.trim
                ? header.trim().replace(/^["']|["']$/g, "")
                : "" // Remove surrounding quotes
          )
          .filter((header) => header !== undefined && header !== null); // Filter out undefined/null headers

        if (headers.length === 0) {
          throw new Error("No valid headers found in CSV file");
        }

        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          try {
            let row = [];
            let inQuotes = false;
            let currentCell = "";

            // Simple CSV parser that handles quoted values
            for (let j = 0; j < lines[i].length; j++) {
              const char = lines[i][j];

              if (char === '"' || char === "'") {
                inQuotes = !inQuotes;
              } else if (char === separator && !inQuotes) {
                row.push(
                  currentCell && currentCell.trim ? currentCell.trim() : ""
                );
                currentCell = "";
              } else {
                currentCell += char;
              }
            }

            // Add the last cell
            row.push(currentCell && currentCell.trim ? currentCell.trim() : "");

            // Ensure row has the same number of columns as headers
            while (row.length < headers.length) {
              row.push("");
            }

            // Only include rows that have at least one non-empty cell
            if (row.some((cell) => cell && String(cell).trim() !== "")) {
              data.push(row.slice(0, headers.length)); // Trim to header length
            }
          } catch (rowError) {
            // Continue with next row instead of failing completely
          }
        }

        return { headers, data };
      } catch (error) {
        console.error("CSV parsing error:", error);
        throw new Error(`Failed to parse CSV file: ${error.message}`);
      }
    },

    validateHeaders(fileHeaders) {
      // Validate that fileHeaders is an array
      if (!Array.isArray(fileHeaders)) {
        return {
          valid: false,
          message: "Invalid headers format: headers must be an array",
        };
      }

      const requiredHeaders = ["Concept", "Topic", "Subtopic"];
      const dynamicHeaders =
        this.plannerHeaders && Array.isArray(this.plannerHeaders)
          ? this.plannerHeaders
              .map((h) => (h && h.header ? h.header : ""))
              .filter((h) => h)
          : [];
      const expectedHeaders = [...requiredHeaders, ...dynamicHeaders];

      // Optional headers that are allowed but not required (like S. No, Serial Number, etc.)
      const optionalHeaders = [
        "S. No",
        "S.No",
        "SNo",
        "Serial No",
        "Serial Number",
        "Sr. No",
        "Sr.No",
        "SrNo",
        "#",
        ...(this.showPlannerTypeColumn ? ["Type"] : []),
      ];

      // Check if all required headers are present
      const missingHeaders = expectedHeaders.filter(
        (header) =>
          header &&
          !fileHeaders.some(
            (fileHeader) =>
              fileHeader &&
              typeof fileHeader === "string" &&
              fileHeader.toLowerCase().trim() === header.toLowerCase().trim()
          )
      );

      if (missingHeaders.length > 0) {
        return {
          valid: false,
          message: `Missing required columns: ${missingHeaders.join(", ")}`,
        };
      }

      // Check for extra headers (excluding optional ones)
      const extraHeaders = fileHeaders.filter((fileHeader) => {
        if (!fileHeader || typeof fileHeader !== "string") return false;

        const isRequired = expectedHeaders.some(
          (expectedHeader) =>
            expectedHeader &&
            expectedHeader.toLowerCase().trim() ===
              fileHeader.toLowerCase().trim()
        );
        const isOptional = optionalHeaders.some(
          (optionalHeader) =>
            optionalHeader &&
            optionalHeader.toLowerCase().trim() ===
              fileHeader.toLowerCase().trim()
        );

        return !isRequired && !isOptional;
      });

      if (extraHeaders.length > 0) {
        return {
          valid: false,
          message: `Unexpected columns found: ${extraHeaders.join(", ")}`,
        };
      }

      return { valid: true, message: "Headers are valid" };
    },

    async validateMissingItems(missingItems) {
      // Build query string from params
      const params = new URLSearchParams({
        db: this.$store.state.userData.db,
        campusID: this.$store.state.userData.cId,
        classID: this.sheetObj.plannerSheet.classID,
        sectionID: this.sheetObj.plannerSheet.sectionID,
        sessionID: this.sheetObj.plannerSheet.sessionID,
        subjectID: this.sheetObj.plannerSheet.subjectID,
        userID: this.$store.state.userData.userID,
      }).toString();
      const url =
        this.$store.state.domain + "PlannerSheet/ValidateData?" + params;
      // Send only the items array as the POST body
      const response = await this.post({
        url,
        body: missingItems,
        token: this.$store.state.userData.token,
        context: this, // Add context so store can access $bvToast
      });

      // If success, auto-complete progress, close modal, and reload dropdowns
      if (response && response.status === "success") {
        this.importProgress.value = 100;
        this.importProgress.message = "Validation successful! Updating data...";
        // Reload dropdowns
        await Promise.all([this.loadConcepts(), this.loadTopics()]);
        // Close modal after short delay
        setTimeout(() => {
          this.showImportModal = false;
        }, 1000);
      }
      return response;
    },

    processImportData(data, headers, collectMissing = false) {
      const errors = [];
      const validRows = [];
      let successCount = 0;
      const missingItems = [];

      // Create header mapping (excluding S. No and similar columns)
      const headerMap = {};
      const optionalHeaders = [
        "S. No",
        "S.No",
        "SNo",
        "Serial No",
        "Serial Number",
        "Sr. No",
        "Sr.No",
        "SrNo",
        "#",
      ];

      headers.forEach((header, index) => {
        // Skip optional headers like S. No
        const isOptional = optionalHeaders.some(
          (optionalHeader) =>
            optionalHeader &&
            optionalHeader.toLowerCase &&
            optionalHeader.toLowerCase().trim() ===
              (header && header.toLowerCase ? header.toLowerCase().trim() : "")
        );

        if (!isOptional && header) {
          headerMap[header.toLowerCase().trim()] = index;
        }
      });

      data.forEach((row, rowIndex) => {
        try {
          // Validate that row is an array and has data
          if (!Array.isArray(row) || row.length === 0) {
            errors.push({
              row: rowIndex + 2, // +2 for header row and 0-based index
              message: "Row is empty or invalid format",
            });
            return;
          }

          const rowData = {
            concept: "",
            topic: "",
            subtopic: "",
            plannerRowHeaders: [],
          };

          if (this.showPlannerTypeColumn) {
            rowData.type = "None";
          }

          // Map basic fields with proper null checks
          const conceptIndex = headerMap["concept"];
          const topicIndex = headerMap["topic"];
          const subtopicIndex = headerMap["subtopic"];
          const typeIndex = headerMap["type"];

          // Safely extract values with null checks
          if (
            conceptIndex !== undefined &&
            row[conceptIndex] !== undefined &&
            row[conceptIndex] !== null
          ) {
            rowData.concept = String(row[conceptIndex]).trim();
          }
          if (
            topicIndex !== undefined &&
            row[topicIndex] !== undefined &&
            row[topicIndex] !== null
          ) {
            rowData.topic = String(row[topicIndex]).trim();
          }
          if (
            subtopicIndex !== undefined &&
            row[subtopicIndex] !== undefined &&
            row[subtopicIndex] !== null
          ) {
            rowData.subtopic = String(row[subtopicIndex]).trim();
          }
          if (
            this.showPlannerTypeColumn &&
            typeIndex !== undefined &&
            row[typeIndex] !== undefined &&
            row[typeIndex] !== null
          ) {
            const importedType = String(row[typeIndex]).trim();
            const validType = this.plannerTypeOptions.find(
              (option) =>
                option.value.toLowerCase() === importedType.toLowerCase()
            );
            rowData.type = validType ? validType.value : "None";
          }

          // Validate required fields
          if (!rowData.concept && !rowData.topic && !rowData.subtopic) {
            errors.push({
              row: rowIndex + 2, // +2 for header row and 0-based index
              message: "Row is empty or missing all required data",
            });
            return;
          }

          // Validate concept, topic, and subtopic against dropdown options
          const validationErrors = [];
          let conceptExists = true,
            topicExists = true,
            subtopicExists = true;

          // Check if concept exists in dropdown options with proper null checks
          if (
            rowData.concept &&
            this.concepts &&
            Array.isArray(this.concepts)
          ) {
            conceptExists = this.concepts.some((c) => {
              if (!c || !c.concept || typeof c.concept !== "string")
                return false;

              const conceptName = c.concept.toLowerCase().trim();
              const importConcept = rowData.concept.toLowerCase().trim();

              // Direct match
              if (conceptName === importConcept) {
                return true;
              }

              // Try matching with class prefix (e.g., "Grade II - Computer System")
              const conceptWithPrefix = `${this.getSelectedClassName} - ${importConcept}`;
              if (conceptName === conceptWithPrefix.toLowerCase().trim()) {
                return true;
              }

              // Try matching without class prefix (e.g., "Computer System" from "Grade II - Computer System")
              const conceptWithoutPrefix = this.cleanConceptText(c.concept)
                .toLowerCase()
                .trim();
              if (conceptWithoutPrefix === importConcept) {
                return true;
              }

              // Try matching the original concept with prefix against cleaned import concept
              const importConceptWithPrefix = `${this.getSelectedClassName} - ${importConcept}`;
              if (
                conceptName === importConceptWithPrefix.toLowerCase().trim()
              ) {
                return true;
              }

              return false;
            });

            if (!conceptExists) {
              validationErrors.push(
                `Concept "${rowData.concept}" not found in dropdown options`
              );
            }
          }

          // Check if topic exists in dropdown options with proper null checks
          if (rowData.topic && this.topics && Array.isArray(this.topics)) {
            topicExists = this.topics.some((t) => {
              if (!t || !t.mainTopic || typeof t.mainTopic !== "string")
                return false;

              const topicName = t.mainTopic.toLowerCase().trim();
              const importTopic = rowData.topic.toLowerCase().trim();

              // Direct match
              if (topicName === importTopic) return true;

              // Try matching with class prefix
              const topicWithPrefix = `${this.getSelectedClassName} - ${importTopic}`;
              if (topicName === topicWithPrefix.toLowerCase().trim())
                return true;

              // Try matching without class prefix
              const topicWithoutPrefix = this.cleanConceptText(t.mainTopic)
                .toLowerCase()
                .trim();
              if (topicWithoutPrefix === importTopic) return true;

              return false;
            });

            if (!topicExists) {
              validationErrors.push(
                `Topic "${rowData.topic}" not found in dropdown options`
              );
            }
          }

          // Check if subtopic exists in dropdown options with proper null checks
          if (
            rowData.subtopic &&
            this.subtopics &&
            Array.isArray(this.subtopics)
          ) {
            subtopicExists = this.subtopics.some((s) => {
              if (!s || !s.subTopic || typeof s.subTopic !== "string")
                return false;

              const subtopicName = s.subTopic.toLowerCase().trim();
              const importSubtopic = rowData.subtopic.toLowerCase().trim();

              // Direct match
              if (subtopicName === importSubtopic) return true;

              // Try matching with class prefix
              const subtopicWithPrefix = `${this.getSelectedClassName} - ${importSubtopic}`;
              if (subtopicName === subtopicWithPrefix.toLowerCase().trim())
                return true;

              // Try matching without class prefix
              const subtopicWithoutPrefix = this.cleanConceptText(s.subTopic)
                .toLowerCase()
                .trim();
              if (subtopicWithoutPrefix === importSubtopic) return true;

              return false;
            });

            if (!subtopicExists) {
              validationErrors.push(
                `Subtopic "${rowData.subtopic}" not found in dropdown options`
              );
            }
          }

          // If there are validation errors, collect missing items if requested
          if (
            collectMissing &&
            (!conceptExists || !topicExists || !subtopicExists)
          ) {
            missingItems.push({
              concept: rowData.concept || "",
              topic: rowData.topic || "",
              subTopic: rowData.subtopic || "",
            });
            return;
          }

          // If not collecting, skip invalid rows
          if (!collectMissing && validationErrors.length > 0) {
            errors.push({
              row: rowIndex + 2,
              message: `Validation failed: ${validationErrors.join(", ")}`,
            });
            return; // Skip this row
          }

          // Map dynamic header values with proper null checks
          if (this.plannerHeaders && Array.isArray(this.plannerHeaders)) {
            this.plannerHeaders.forEach((plannerHeader) => {
              if (plannerHeader && plannerHeader.header) {
                const headerIndex =
                  headerMap[plannerHeader.header.toLowerCase().trim()];
                if (
                  headerIndex !== undefined &&
                  row[headerIndex] !== undefined &&
                  row[headerIndex] !== null
                ) {
                  rowData.plannerRowHeaders.push({
                    headerID: plannerHeader.id,
                    value: String(row[headerIndex]).trim(),
                  });
                } else {
                  rowData.plannerRowHeaders.push({
                    headerID: plannerHeader.id,
                    value: "",
                  });
                }
              }
            });
          }

          validRows.push(rowData);
          successCount++;
        } catch (error) {
          console.error(`Error processing row ${rowIndex + 2}:`, error);
          errors.push({
            row: rowIndex + 2,
            message: `Error processing row: ${error.message}`,
          });
        }
      });

      if (collectMissing) {
        return missingItems;
      }

      return {
        validRows,
        successCount,
        errors,
      };
    },

    // Method to check if content contains URLs
    hasUrls(text) {
      if (!text) return false;
      const urlRegex =
        /(@?https?:\/\/[^\s]+|@?www\.[^\s]+|@?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*)/gi;
      return urlRegex.test(text);
    },

    // Method to convert URLs to anchor tags for saving
    convertUrlsToAnchorTags(text) {
      if (!text) return "";

      // Enhanced regex to match various URL patterns
      const urlRegex =
        /(@?https?:\/\/[^\s]+|@?www\.[^\s]+|@?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*)/gi;

      return text.replace(urlRegex, (match) => {
        // Remove @ if present and create the actual URL
        const actualUrl = match.startsWith("@") ? match.substring(1) : match;
        const displayText = match;

        // Ensure URL has protocol
        const finalUrl = actualUrl.startsWith("http")
          ? actualUrl
          : `https://${actualUrl}`;

        return `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer">${displayText}</a>`;
      });
    },

    // Method to strip HTML tags for editing
    stripHtmlTags(text) {
      if (!text) return "";

      // Create a temporary div to parse HTML and extract text content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = text;
      return tempDiv.textContent || tempDiv.innerText || "";
    },

    // Method to process content before saving
    processContentForSaving(content) {
      if (!content) return "";
      return this.convertUrlsToAnchorTags(content);
    },

    // Method to process content for editing
    processContentForEditing(content) {
      if (!content) return "";
      return this.stripHtmlTags(content);
    },

    onTextareaInput(event, headers, headerId) {
      headers[headerId] = event.target.value;
    },

    // Upload Show related methods
    openUploadShowModal() {
      this.showUploadShowModal = true;
    },

    resetUploadShowModal() {
      this.uploadShowFile = null;
      this.uploadShowFileState = null;
      if (this.$refs.uploadShowInput) {
        this.$refs.uploadShowInput.value = "";
      }
      this.uploadShowProgress = {
        show: false,
        value: 0,
        max: 100,
        message: "",
        variant: "primary",
      };
      this.uploadShowResults = {
        show: false,
        success: false,
        message: "",
        link: "",
      };
      this.uploadShowProcessing = false;
      // Don't clear uploadedFileLink here as we want to keep it for Preview Show button
    },

    handleUploadShowInputChange(event) {
      const file = event.target.files && event.target.files[0];
      this.uploadShowFile = file || null;
      this.onUploadShowFileSelected();
    },

    onUploadShowFileSelected() {
      if (this.uploadShowFile) {
        // Validate file type
        const allowedExtensions = [".pdf"];
        const fileExtension = this.uploadShowFile.name
          .toLowerCase()
          .substr(this.uploadShowFile.name.lastIndexOf("."));

        if (allowedExtensions.includes(fileExtension)) {
          // Check file size (10MB limit)
          const maxSize = 10 * 1024 * 1024; // 10MB in bytes
          if (this.uploadShowFile.size <= maxSize) {
            this.uploadShowFileState = true;
          } else {
            this.uploadShowFileState = false;
            this.$bvToast.toast("File size must be less than 10MB.", {
              title: "File Too Large",
              variant: "warning",
              solid: true,
            });
          }
        } else {
          this.uploadShowFileState = false;
          this.$bvToast.toast("Please select a valid PDF file.", {
            title: "Invalid File Type",
            variant: "danger",
            solid: true,
          });
        }
      } else {
        this.uploadShowFileState = null;
      }
    },

    async processUploadShow() {
      if (!this.uploadShowFile) {
        this.$bvToast.toast("Please select a file to upload.", {
          title: "No File Selected",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.uploadShowProcessing = true;
      this.uploadShowProgress.show = true;
      this.uploadShowProgress.value = 0;
      this.uploadShowProgress.message = "Preparing upload...";
      this.uploadShowResults.show = false;

      try {
        // Step 1: Upload file to get the link
        this.uploadShowProgress.value = 10;
        this.uploadShowProgress.message = "Uploading file...";

        const uploaded = await uploadToMySkool([this.uploadShowFile]);
        const fileLink = getUploadPath(uploaded[0]);

        if (!fileLink) {
          throw new Error("Failed to get file link from upload service");
        }

        this.uploadShowProgress.value = 50;

        this.uploadShowProgress.value = 60;
        this.uploadShowProgress.message = "Sending SOW data to server...";

        // Step 2: Send the file link to the SOW API
        const params = new URLSearchParams({
          campusID: this.$store.state.userData.cId,
          id: this.sheetObj.plannerSheet.id || 0,
          db: this.$store.state.userData.db,
          userID:
            this.$store.state.userData.userID ||
            this.$store.state.userData.id ||
            1,
        });

        // Construct the API URL
        const apiUrl = `${
          this.$store.state.domain
        }PlannerSheet/UploadSow?${params.toString()}`;

        this.uploadShowProgress.value = 80;
        this.uploadShowProgress.message = "Processing SOW data...";

        // Make the API call with the file link as JSON string
        const response = await axios.post(apiUrl, JSON.stringify(fileLink), {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        this.uploadShowProgress.value = 100;
        this.uploadShowProgress.message = "Upload completed!";
        this.uploadShowProgress.variant = "success";

        // Handle response
        if (response.data && response.data.status === "success") {
          this.uploadShowResults = {
            show: true,
            success: true,
            message: "SOW uploaded successfully!",
            link: response.data.link || response.data.url || fileLink,
          };

          // Store the file link for Preview Show button
          this.uploadedFileLink = this.uploadShowResults.link;

          this.$bvToast.toast("SOW uploaded successfully!", {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });

          // Auto-close modal after 3 seconds
          setTimeout(() => {
            this.showUploadShowModal = false;
          }, 3000);
        } else {
          throw new Error(response.data?.message || "Upload failed");
        }
      } catch (error) {
        console.error("SOW Upload error:", error);
        this.uploadShowProgress.variant = "danger";
        this.uploadShowProgress.message = "Upload failed!";

        this.uploadShowResults = {
          show: true,
          success: false,
          message:
            error.response?.data?.message ||
            error.message ||
            "An error occurred while uploading the SOW.",
          link: "",
        };

        this.$bvToast.toast(
          error.response?.data?.message ||
            error.message ||
            "SOW upload failed!",
          {
            title: "Upload Error",
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.uploadShowProcessing = false;
      }
    },

    copyLink() {
      if (this.uploadShowResults.link) {
        navigator.clipboard
          .writeText(this.uploadShowResults.link)
          .then(() => {
            this.$bvToast.toast("Link copied to clipboard!", {
              title: "Copied",
              variant: "success",
              solid: true,
            });
          })
          .catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = this.uploadShowResults.link;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);

            this.$bvToast.toast("Link copied to clipboard!", {
              title: "Copied",
              variant: "success",
              solid: true,
            });
          });
      }
    },

    openLink() {
      if (this.uploadShowResults.link) {
        window.open(this.uploadShowResults.link, "_blank");
      }
    },

    previewShow() {
      if (this.uploadedFileLink) {
        window.open(this.uploadedFileLink, "_blank", "noopener,noreferrer");
      }
    },

    // Upload Urdu related methods
    openUploadUrduModal() {
      this.showUploadUrduModal = true;
    },

    resetUploadUrduModal() {
      this.uploadUrduFile = null;
      this.uploadUrduFileState = null;
      if (this.$refs.uploadUrduInput) {
        this.$refs.uploadUrduInput.value = "";
      }
      this.uploadUrduProgress = {
        show: false,
        value: 0,
        max: 100,
        message: "",
        variant: "primary",
      };
      this.uploadUrduResults = {
        show: false,
        success: false,
        message: "",
        link: "",
      };
      this.uploadUrduProcessing = false;
      // Don't clear uploadedUrduLink here as we want to keep it for Preview Urdu button
    },

    handleUploadUrduInputChange(event) {
      const file = event.target.files && event.target.files[0];
      this.uploadUrduFile = file || null;
      this.onUploadUrduFileSelected();
    },

    onUploadUrduFileSelected() {
      if (this.uploadUrduFile) {
        // Validate file type
        const allowedExtensions = [".pdf"];
        const fileExtension = this.uploadUrduFile.name
          .toLowerCase()
          .substr(this.uploadUrduFile.name.lastIndexOf("."));

        if (allowedExtensions.includes(fileExtension)) {
          // Check file size (10MB limit)
          const maxSize = 10 * 1024 * 1024; // 10MB in bytes
          if (this.uploadUrduFile.size <= maxSize) {
            this.uploadUrduFileState = true;
          } else {
            this.uploadUrduFileState = false;
            this.$bvToast.toast("File size must be less than 10MB.", {
              title: "File Too Large",
              variant: "warning",
              solid: true,
            });
          }
        } else {
          this.uploadUrduFileState = false;
          this.$bvToast.toast("Please select a valid PDF file.", {
            title: "Invalid File Type",
            variant: "danger",
            solid: true,
          });
        }
      } else {
        this.uploadUrduFileState = null;
      }
    },

    async processUploadUrdu() {
      if (!this.uploadUrduFile) {
        this.$bvToast.toast("Please select a file to upload.", {
          title: "No File Selected",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.uploadUrduProcessing = true;
      this.uploadUrduProgress.show = true;
      this.uploadUrduProgress.value = 0;
      this.uploadUrduProgress.message = "Preparing upload...";
      this.uploadUrduResults.show = false;

      try {
        // Step 1: Upload file to get the link
        this.uploadUrduProgress.value = 10;
        this.uploadUrduProgress.message = "Uploading file...";

        const uploaded = await uploadToMySkool([this.uploadUrduFile]);
        const fileLink = getUploadPath(uploaded[0]);

        if (!fileLink) {
          throw new Error("Failed to get file link from upload service");
        }

        this.uploadUrduProgress.value = 50;

        this.uploadUrduProgress.value = 60;
        this.uploadUrduProgress.message = "Sending Urdu data to server...";

        // Step 2: Send the file link to the Urdu API
        const params = new URLSearchParams({
          campusID: this.$store.state.userData.cId,
          id: this.sheetObj.plannerSheet.id || 0,
          db: this.$store.state.userData.db,
          userID:
            this.$store.state.userData.userID ||
            this.$store.state.userData.id ||
            1,
        });

        // Construct the API URL
        const apiUrl = `${
          this.$store.state.domain
        }PlannerSheet/UploadUrduLink?${params.toString()}`;

        this.uploadUrduProgress.value = 80;
        this.uploadUrduProgress.message = "Processing Urdu data...";

        // Make the API call with the file link as JSON string
        const response = await axios.post(apiUrl, JSON.stringify(fileLink), {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        this.uploadUrduProgress.value = 100;
        this.uploadUrduProgress.message = "Upload completed!";
        this.uploadUrduProgress.variant = "success";

        // Handle response
        if (response.data && response.data.status === "success") {
          // Extract Urdu link from response (similar to SOW - could be link, url, or urduLink)
          const urduLink =
            response.data.urduLink ||
            response.data.link ||
            response.data.url ||
            fileLink;

          this.uploadUrduResults = {
            show: true,
            success: true,
            message: "Urdu uploaded successfully!",
            link: urduLink,
          };

          // Store the file link for Preview Urdu button
          this.uploadedUrduLink = urduLink;

          this.$bvToast.toast("Urdu uploaded successfully!", {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });

          // Auto-close modal after 3 seconds
          setTimeout(() => {
            this.showUploadUrduModal = false;
          }, 3000);
        } else {
          throw new Error(response.data?.message || "Upload failed");
        }
      } catch (error) {
        console.error("Urdu Upload error:", error);
        this.uploadUrduProgress.variant = "danger";
        this.uploadUrduProgress.message = "Upload failed!";

        this.uploadUrduResults = {
          show: true,
          success: false,
          message:
            error.response?.data?.message ||
            error.message ||
            "An error occurred while uploading the Urdu file.",
          link: "",
        };

        this.$bvToast.toast(
          error.response?.data?.message ||
            error.message ||
            "Urdu upload failed!",
          {
            title: "Upload Error",
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.uploadUrduProcessing = false;
      }
    },

    previewUrdu() {
      if (this.uploadedUrduLink) {
        window.open(this.uploadedUrduLink, "_blank", "noopener,noreferrer");
      }
    },

    copyUrduLink() {
      if (this.uploadUrduResults.link) {
        navigator.clipboard
          .writeText(this.uploadUrduResults.link)
          .then(() => {
            this.$bvToast.toast("Link copied to clipboard!", {
              title: "Copied",
              variant: "success",
              solid: true,
            });
          })
          .catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = this.uploadUrduResults.link;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);

            this.$bvToast.toast("Link copied to clipboard!", {
              title: "Copied",
              variant: "success",
              solid: true,
            });
          });
      }
    },

    openUrduLink() {
      if (this.uploadUrduResults.link) {
        window.open(this.uploadUrduResults.link, "_blank");
      }
    },
  },
  watch: {
    // Watch for changes in class selection to reload concepts
    "sheetObj.plannerSheet.classID"() {
      if (
        this.sheetObj.plannerSheet.classID &&
        this.sheetObj.plannerSheet.subjectID
      ) {
        this.loadConcepts();
      }
    },
    // Watch for changes in subject selection to reload concepts
    "sheetObj.plannerSheet.subjectID"() {
      if (
        this.sheetObj.plannerSheet.classID &&
        this.sheetObj.plannerSheet.subjectID
      ) {
        this.loadConcepts();
      }
    },
    // Watch for changes in section selection to reload concepts
    "sheetObj.plannerSheet.sectionID"() {
      if (
        this.sheetObj.plannerSheet.classID &&
        this.sheetObj.plannerSheet.subjectID
      ) {
        this.loadConcepts();
      }
    },
    // Watch for changes in concepts data
    concepts: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
        }
      },
      immediate: true,
      deep: true,
    },
    // Watch for changes in topics data
    topics: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
        }
      },
      immediate: true,
      deep: true,
    },
    // Watch for changes in subtopics data
    subtopics: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped>
.simple-planner-table {
  width: 100%;
  position: relative;
}

.table-responsive-wrapper {
  position: relative;
  height: 650px;
  overflow: hidden;
  border: 1px solid #e3e6f0;
  border-radius: 0.75rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(139, 92, 246, 0.15);
  background: white;
}

.table-scroll-container {
  width: 100%;
  height: 100%;
  overflow: auto !important;
  overflow-x: scroll !important;
  overflow-y: scroll !important;
  position: relative;
  border-radius: 0.75rem;
  scrollbar-gutter: stable both-edges !important;
}

.table-scroll-container::-webkit-scrollbar {
  width: 12px !important;
  height: 12px !important;
  background: #f1f3f4 !important;
}

.table-scroll-container::-webkit-scrollbar-track {
  background: #f1f3f4 !important;
  border-radius: 6px !important;
  border: 1px solid #e9ecef !important;
}

.table-scroll-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%) !important;
  border-radius: 6px !important;
  border: 1px solid #f1f3f4 !important;
  transition: all 0.3s ease !important;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #7c3aed 0%, #6d28d9 100%) !important;
  border: 1px solid #6d28d9 !important;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2) !important;
}

.table-scroll-container::-webkit-scrollbar-corner {
  background: #f1f3f4 !important;
  border-radius: 6px !important;
}

/* Firefox scrollbar support */
.table-scroll-container {
  scrollbar-width: auto !important;
  scrollbar-color: #8b5cf6 #f1f3f4 !important;
}

/* Ensure table has proper minimum width for horizontal scrolling */
.planner-table {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  min-width: 1400px !important;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

/* Force scrollbars to always be visible */
.table-responsive-wrapper {
  position: relative;
  height: 650px;
  overflow: hidden;
  border: 1px solid #e3e6f0;
  border-radius: 0.75rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(139, 92, 246, 0.15);
  background: white;
}

/* Ensure the table container shows scrollbars */
.simple-planner-table {
  width: 100%;
  position: relative;
}

/* Make sure all columns have proper widths that require horizontal scrolling */
.concept-col {
  width: 320px !important;
  min-width: 320px !important;
}

.topic-col {
  width: 380px !important;
  min-width: 380px !important;
}

.subtopic-col {
  width: 450px !important;
  min-width: 450px !important;
}

.type-col {
  width: 180px !important;
  min-width: 180px !important;
}

.header-col {
  min-width: 300px !important;
  width: 300px !important;
}

.actions-col {
  width: 90px !important;
  min-width: 90px !important;
  text-align: center !important;
  padding: 8px !important;
  position: sticky !important;
  right: 0 !important;
  background: white !important;
  z-index: 15 !important;
  border-left: 3px solid #8b5cf6 !important;
  box-shadow: -2px 0 5px rgba(139, 92, 246, 0.15) !important;
}

.sticky-column {
  position: sticky !important;
  background: white !important;
  z-index: 5 !important;
  border-right: 2px solid #e3e6f0 !important;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05) !important;
}

.sticky-right {
  position: sticky !important;
  right: 0 !important;
  background: white !important;
  z-index: 5 !important;
  border-left: 3px solid #e3e6f0 !important;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05) !important;
}

.data-row {
  height: 70px !important;
  transition: all 0.2s ease !important;
}

.data-row:hover {
  background-color: #f3f0ff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.1);
}

.data-row td {
  padding: 12px !important;
  vertical-align: middle !important;
  color: #5a5c69 !important;
  background: white !important;
  border: 1px solid #e3e6f0 !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
}

.data-row:nth-child(even) td {
  background: #fafbfc !important;
}

.data-row:hover td {
  background: #f3f0ff !important;
}

.data-row:nth-child(even) td.actions-col {
  background: #fafbfc !important;
}

.data-row:nth-child(even):hover td.actions-col {
  background: #f3f0ff !important;
}

/* CRITICAL: Ultra-high z-index for open dropdowns */
.planner-sheet-form .simple-select.vs--open .vs__dropdown-menu {
  z-index: 999999999 !important;
  position: absolute !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Ensure dropdown container has proper stacking */
.planner-sheet-form .simple-select {
  position: relative !important;
  z-index: 1 !important;
}

.planner-sheet-form .simple-select.vs--open {
  z-index: 999999998 !important;
  position: relative !important;
}

/* Dropdown toggle styling */
.planner-sheet-form .simple-select .vs__dropdown-toggle {
  z-index: 999999997 !important;
  position: relative !important;
  background: white !important;
  border: 2px solid #e3e6f0 !important;
  border-radius: 0.5rem !important;
  min-height: 50px !important;
}

.planner-sheet-form .simple-select.vs--open .vs__dropdown-toggle {
  border-color: #8b5cf6 !important;
  box-shadow: 0 0 0 0.2rem rgba(139, 92, 246, 0.25) !important;
  z-index: 999999997 !important;
}

/* Override table and container z-indexes to allow dropdowns */
.planner-sheet-form .table-scroll-container {
  position: relative !important;
  z-index: 1 !important;
  /* Allow overflow for dropdowns */
}

.planner-sheet-form .data-row {
  position: relative !important;
  z-index: 2 !important;
}

.planner-sheet-form .data-row td {
  position: relative !important;
  z-index: 3 !important;
  overflow: visible !important;
}

/* When a dropdown is open, prioritize the entire row */
.planner-sheet-form .data-row:has(.simple-select.vs--open) {
  z-index: 999999996 !important;
}

/* Table header z-indexes (lower than dropdowns) */
.planner-sheet-form .table thead th {
  z-index: 10 !important;
}

.planner-sheet-form .table thead th.actions-col {
  z-index: 20 !important;
}

/* Enhanced dropdown options styling */
.planner-sheet-form .v-select .vs__dropdown-option,
.planner-sheet-form .simple-select .vs__dropdown-option {
  padding: 12px 16px !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
  color: #5a5c69 !important;
  background: white !important;
  border-bottom: 1px solid #f1f3f4 !important;
  transition: all 0.2s ease !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.planner-sheet-form .v-select .vs__dropdown-option:hover,
.planner-sheet-form .simple-select .vs__dropdown-option:hover {
  background: #f3f0ff !important;
  color: #8b5cf6 !important;
  font-weight: 500 !important;
}

.planner-sheet-form .v-select .vs__dropdown-option--highlight,
.planner-sheet-form .simple-select .vs__dropdown-option--highlight {
  background: #8b5cf6 !important;
  color: white !important;
  font-weight: 600 !important;
}

.planner-sheet-form .v-select .vs__dropdown-option--selected,
.planner-sheet-form .simple-select .vs__dropdown-option--selected {
  background: #e6d7ff !important;
  color: #6d28d9 !important;
  font-weight: 600 !important;
}

.planner-sheet-form .v-select .vs__dropdown-menu::-webkit-scrollbar,
.planner-sheet-form .simple-select .vs__dropdown-menu::-webkit-scrollbar {
  width: 14px !important;
  background: #f1f3f4 !important;
  border-radius: 7px !important;
  display: block !important;
  visibility: visible !important;
}

/* ============ TABLE HEADER STYLING FIXES ============ */

/* Apply to all table headers specifically */
.planner-sheet-form .table thead th,
.planner-sheet-form .table-responsive-wrapper thead th,
.planner-sheet-form .table-scroll-container thead th {
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
  border: none !important;
  padding: 16px 12px !important;
  font-weight: 600 !important;
  text-align: center !important;
  font-size: 13px !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2) !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* Target each column type specifically */
.planner-sheet-form thead th.concept-col,
.planner-sheet-form thead th.topic-col,
.planner-sheet-form thead th.subtopic-col,
.planner-sheet-form thead th.type-col,
.planner-sheet-form thead th.header-col,
.planner-sheet-form thead th.actions-col {
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
  border: none !important;
  font-weight: 600 !important;
}

/* Override any Bootstrap defaults */
.planner-sheet-form .table thead th,
.planner-sheet-form .thead-light th {
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Ensure header text is white */
.planner-sheet-form .table thead th,
.planner-sheet-form .table thead th .header-content {
  color: white !important;
  font-weight: 600 !important;
  text-shadow: none !important;
}

/* ============ ACTIONS COLUMN STICKY POSITIONING ============ */

/* Ensure actions column header is sticky on the right */
.planner-sheet-form thead th.actions-col {
  position: sticky !important;
  right: 0 !important;
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%) !important;
  z-index: 20 !important;
  border-left: 3px solid #7c3aed !important;
  box-shadow: -2px 0 5px rgba(139, 92, 246, 0.2) !important;
}

/* Ensure actions column cells are sticky on the right */
.planner-sheet-form .data-row td.actions-col {
  position: sticky !important;
  right: 0 !important;
  background: white !important;
  z-index: 15 !important;
  border-left: 3px solid #8b5cf6 !important;
  box-shadow: -2px 0 5px rgba(139, 92, 246, 0.15) !important;
}

.planner-sheet-form .data-row:hover td.actions-col {
  background: #f3f0ff !important;
}

.planner-sheet-form .data-row:nth-child(even) td.actions-col {
  background: #fafbfc !important;
}

.planner-sheet-form .data-row:nth-child(even):hover td.actions-col {
  background: #f3f0ff !important;
}

/* ============ ENHANCED ACTIONS COLUMN STYLING ============ */

.actions-col {
  width: 90px !important;
  min-width: 90px !important;
  text-align: center !important;
  padding: 8px !important;
  position: sticky !important;
  right: 0 !important;
  background: white !important;
  z-index: 15 !important;
  border-left: 3px solid #8b5cf6 !important;
  box-shadow: -2px 0 5px rgba(139, 92, 246, 0.15) !important;
}

/* Enhanced button styling for actions */
.planner-sheet-form .btn-icon {
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 50% !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.planner-sheet-form .btn-icon:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

/* ============ TABLE SCROLLBAR BACKGROUND MATCHING ============ */

/* Ensure table container background matches scrollbar area */
.planner-sheet-form .table-scroll-container {
  background: white !important;
  border-radius: 0.75rem !important;
}

.planner-sheet-form .table-responsive-wrapper {
  background: white !important;
  border-radius: 0.75rem !important;
}

.planner-sheet-form .table {
  background: white !important;
  margin-bottom: 0 !important;
}

/* Ensure consistent background throughout */
.planner-sheet-form .table tbody tr td {
  background: white !important;
}

.planner-sheet-form .table tbody tr:nth-child(even) td {
  background: #fafbfc !important;
}

.planner-sheet-form .table tbody tr:hover td {
  background: #f3f0ff !important;
}

/* ============ DROPDOWN MENU STYLING - KEEPING UNCHANGED ============ */

.planner-sheet-form .v-select .vs__dropdown-menu,
.planner-sheet-form .simple-select .vs__dropdown-menu {
  max-height: 200px !important;
  overflow-y: scroll !important;
  overflow-x: hidden !important;
  border: 2px solid #8b5cf6 !important;
  border-radius: 8px !important;
  box-shadow: 0 15px 35px rgba(139, 92, 246, 0.4) !important;
  background: white !important;
  z-index: 999999999 !important;
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  min-width: 250px !important;

  /* Firefox scrollbar - ENHANCED */
  scrollbar-width: auto !important;
  scrollbar-color: #8b5cf6 #f1f3f4 !important;
}

.planner-sheet-form .v-select .vs__dropdown-menu::-webkit-scrollbar-track,
.planner-sheet-form .simple-select .vs__dropdown-menu::-webkit-scrollbar-track {
  background: #f1f3f4 !important;
  border-radius: 7px !important;
  margin: 2px !important;
  border: 1px solid #e9ecef !important;
  visibility: visible !important;
}

.planner-sheet-form .v-select .vs__dropdown-menu::-webkit-scrollbar-thumb,
.planner-sheet-form .simple-select .vs__dropdown-menu::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%) !important;
  border-radius: 7px !important;
  border: 1px solid #f1f3f4 !important;
  min-height: 50px !important;
  transition: all 0.3s ease !important;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15) !important;
  visibility: visible !important;
}

.planner-sheet-form .v-select .vs__dropdown-menu::-webkit-scrollbar-thumb:hover,
.planner-sheet-form
  .simple-select
  .vs__dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #7c3aed 0%, #6d28d9 100%) !important;
  border: 1px solid #6d28d9 !important;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.25) !important;
}

.planner-sheet-form .v-select .vs__dropdown-menu::-webkit-scrollbar-corner,
.planner-sheet-form
  .simple-select
  .vs__dropdown-menu::-webkit-scrollbar-corner {
  background: #f1f3f4 !important;
  border-radius: 7px !important;
}

/* Import Modal Styles */
.import-content {
  max-height: 70vh;
  overflow-y: auto;
}

.upload-section {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 20px;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
}

.upload-section h6 {
  text-align: center;
}

.upload-file-wrapper {
  text-align: left;
}

.planner-file-picker__field {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 2.75rem;
  padding: 0.5rem 0.5rem 0.5rem 0.875rem;
  background: #fff;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.planner-file-picker--valid .planner-file-picker__field {
  border-color: #28c76f;
}

.planner-file-picker--invalid .planner-file-picker__field {
  border-color: #ea5455;
}

.planner-file-picker__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.5;
  font-size: 0.95rem;
  color: #5e5873;
}

.planner-file-picker__btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.upload-section .file-help-text {
  line-height: 1.4;
}

.upload-section:hover {
  border-color: #7367f0;
  background-color: #f5f3ff;
}

.headers-info {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.expected-headers {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.expected-headers .badge {
  font-size: 0.875rem;
  padding: 8px 12px;
}

.available-options {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.option-group {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e9ecef;
  height: 100%;
}

.option-group h6 {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 10px;
  border-bottom: 2px solid currentColor;
  padding-bottom: 5px;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.option-item {
  font-size: 0.8rem;
  color: #6c757d;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.option-item:hover {
  background: #e9ecef;
  color: #495057;
}

.import-progress {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.import-results {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.error-details {
  max-height: 300px;
  overflow-y: auto;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-close {
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* File input styling */
.custom-file-input:focus ~ .custom-file-label {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

/* Progress bar custom colors */
.progress-bar {
  transition: width 0.6s ease;
}
/* Modal header styling */
.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

.modal-title {
  margin-bottom: 0;
  line-height: 1.5;
  color: #6e6b7b;
  font-weight: 600;
}

/* Alert styling within modal */
.import-content .alert {
  border-radius: 6px;
  margin-bottom: 1rem;
}

.import-content .alert-info {
  background-color: #e8f4fd;
  border-color: #7db3d3;
  color: #004085;
}

.import-content .alert-success {
  background-color: #d1f2eb;
  border-color: #7dd3af;
  color: #155724;
}

.import-content .alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

/* List group styling */
.list-group-item {
  border-radius: 6px !important;
  margin-bottom: 4px;
  border: 1px solid #dee2e6;
}

.list-group-item.list-group-item-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

/* Scrollbar for error details */
.error-details::-webkit-scrollbar {
  width: 6px;
}

.error-details::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.error-details::-webkit-scrollbar-thumb {
  background: #7367f0;
  border-radius: 3px;
}

.error-details::-webkit-scrollbar-thumb:hover {
  background: #5e50ee;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .import-content {
    max-height: 60vh;
  }

  .expected-headers {
    justify-content: center;
  }

  .upload-section {
    padding: 15px;
  }

  .headers-info,
  .import-progress,
  .import-results {
    padding: 15px;
  }
}

/* Action buttons styling */
.action-buttons {
  display: flex !important;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
}

.action-buttons .btn {
  display: inline-flex !important;
  align-items: center;
  white-space: nowrap;
  min-width: auto;
}

/* Ensure header section is properly laid out */
.d-flex.justify-content-between.align-items-center {
  flex-wrap: wrap;
  gap: 1rem;
}

/* Make sure the title and buttons container is properly visible */
.d-flex.justify-content-between.align-items-center .mb-0 {
  flex-grow: 1;
  margin-bottom: 0 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .d-flex.justify-content-between.align-items-center {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-buttons {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 576px) {
  .expected-headers {
    justify-content: center;
  }

  .upload-section {
    padding: 15px;
  }

  .headers-info,
  .import-progress,
  .import-results {
    padding: 15px;
  }
}

/* Upload Show Modal Styles */
.upload-show-content {
  max-height: 70vh;
  overflow-y: auto;
}

.uploaded-link-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 2px solid #d4edda;
}

.link-container {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e9ecef;
}

.link-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.link-actions .btn {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* Upload Show button styling */
.btn-outline-info {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn-outline-info:focus {
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

/* Progress bar for upload show */
.upload-progress .progress {
  height: 8px;
  border-radius: 4px;
}

.upload-progress .progress-bar {
  border-radius: 4px;
  transition: width 0.6s ease;
}

/* File input styling for upload show */
.upload-show-content .planner-file-picker__field:focus-within {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

/* Alert styling within upload show modal */
.upload-show-content .alert {
  border-radius: 6px;
  margin-bottom: 1rem;
}

.upload-show-content .alert-success {
  background-color: #d1f2eb;
  border-color: #7dd3af;
  color: #155724;
}

.upload-show-content .alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

/* Responsive adjustments for upload show */
@media (max-width: 768px) {
  .upload-show-content {
    max-height: 60vh;
  }

  .link-actions {
    flex-direction: column;
  }

  .link-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
